import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { kpiApi, storeMappingsApi, storesApi, budgetsApi, brandsApi, healthApi, areaManagersApi, areasApi, reportsApi, adminApi, healthScoreConfigApi, googleReviewsApi, inventoryApi, staffApi } from '../lib/api';
import { setThresholds as setSemaphoreThresholds } from '../lib/semaphore';
import { setHealthScoreConfig } from '../lib/health';
import { useAuth } from '../lib/auth';
import { PermissionsMatrixPanel } from '../components/PermissionsMatrixPanel';
import { AreaManagersEditor } from '../components/AreaManagersEditor';
import StaffCombobox from '../components/StaffCombobox';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Save, MapPin, BarChart3, Wallet, Wand2, AlertTriangle, Tag, Plus, Trash2, Activity, CheckCircle2, XCircle, RefreshCw, Pencil, Power, PowerOff, Mail, Send, Loader2, Calendar, Upload, FileText, Gauge, RotateCcw, Star, Package, Lock, ShieldCheck, Link2, Search, Download, GraduationCap, EyeOff } from 'lucide-react';
import BrandDailyConfigPanel from '../components/BrandDailyConfigPanel';
import OrdersCatalogSettings from '../components/OrdersCatalogSettings';
import TrainingSettingsPanel from '../components/TrainingSettingsPanel';
import ImportsPage from './ImportsPage';
import DataHealthPage from './DataHealthPage';
import AuditLogPage from './AuditLogPage';
import { toast } from 'sonner';

const SETUP_FIELD_LABELS = {
  available_hours: 'Ore disponibili (default mensili)',
  avg_ticket: 'Ticket medio (default)',
  service_revenue_pct: '% vendite servizi',
  product_revenue_pct: '% vendite prodotti',
  avg_service_value: 'Valore medio servizio',
  avg_product_value: 'Valore medio prodotto',
  occupancy_pct: '% occupazione',
  review_count: 'Numero recensioni',
  new_client_pct: '% clienti nuovi',
  returning_client_rate: 'Tasso clienti di ritorno',
  memberships_sold: 'Abbonamenti venduti',
};

export default function SettingsPage() {
  // Deep link `?tab=imports|data-health|mappings|audit|...` — la sidebar
  // usa questi link per "approdare" sul tab giusto dopo aver promosso
  // Import/Stato Dati/Mappatura/Audit a tab interni (refactor 2026-05-08
  // idea_d61fb5573eba). Default: 'budget-setup'.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialTab = tabFromUrl || 'budget-setup';
  const [activeTab, setActiveTab] = useState(initialTab);
  const { user } = useAuth();
  const isOps = user?.role === 'operations_manager';

  // Sincronizza URL → state quando l'utente naviga tramite link/redirect.
  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) setActiveTab(tabFromUrl);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFromUrl]);

  const handleTabChange = (v) => {
    setActiveTab(v);
    // Replace URL (no history bloat) so deep-link funziona e copy-link
    // produce link al tab corretto.
    if (v === 'budget-setup') {
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    } else {
      searchParams.set('tab', v);
      setSearchParams(searchParams, { replace: true });
    }
  };

  // Page-level RBAC gate (2026-05-25): /settings è OPS-only. Le scritture
  // sono già protette lato backend (403 su PUT/POST/DELETE), ma fino ad
  // oggi un AM/SU poteva approdare qui via URL diretto e sbirciare la
  // configurazione globale (KPI, soglie, audit, brand-daily, Danger Zone).
  // Il guard è DOPO tutti gli hook (regola React rules-of-hooks).
  if (user && !isOps) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="p-4 md:p-8 max-w-[1920px]" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Impostazioni</h1>
        <p className="text-slate-500 mt-1">Configurazione KPI, budget, mappatura, import dati e diagnostica</p>
      </div>
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="rounded-none bg-slate-100 h-10 w-full overflow-x-auto justify-start whitespace-nowrap">
          <TabsTrigger value="budget-setup" className="rounded-none data-[state=active]:bg-white" data-testid="tab-budget-setup">
            <Wallet className="w-4 h-4 mr-2" /> Setup Budget
          </TabsTrigger>
          <TabsTrigger value="kpi" className="rounded-none data-[state=active]:bg-white" data-testid="tab-kpi">
            <BarChart3 className="w-4 h-4 mr-2" /> Definizioni KPI
          </TabsTrigger>
          {isOps && (
            <TabsTrigger value="health-score" className="rounded-none data-[state=active]:bg-white" data-testid="tab-health-score">
              <Gauge className="w-4 h-4 mr-2" /> Health Score
            </TabsTrigger>
          )}
          <TabsTrigger value="brands" className="rounded-none data-[state=active]:bg-white" data-testid="tab-brands">
            <Tag className="w-4 h-4 mr-2" /> Brand
          </TabsTrigger>
          <TabsTrigger value="areas" className="rounded-none data-[state=active]:bg-white" data-testid="tab-areas">
            <MapPin className="w-4 h-4 mr-2" /> Aree
          </TabsTrigger>
          {isOps && (
            <TabsTrigger value="inventory-cat" className="rounded-none data-[state=active]:bg-white" data-testid="tab-inventory-cat">
              <Package className="w-4 h-4 mr-2" /> Categorie Magazzino
            </TabsTrigger>
          )}
          <TabsTrigger value="mappings" className="rounded-none data-[state=active]:bg-white" data-testid="tab-mappings">
            <MapPin className="w-4 h-4 mr-2" /> Mappatura Negozi
          </TabsTrigger>
          <TabsTrigger value="brand-daily" className="rounded-none data-[state=active]:bg-white" data-testid="tab-brand-daily">
            <Calendar className="w-4 h-4 mr-2" /> Pesi Giornalieri
          </TabsTrigger>
          <TabsTrigger value="imports" className="rounded-none data-[state=active]:bg-white" data-testid="tab-imports">
            <Upload className="w-4 h-4 mr-2" /> Import Dati
          </TabsTrigger>
          <TabsTrigger value="data-health" className="rounded-none data-[state=active]:bg-white" data-testid="tab-data-health">
            <Activity className="w-4 h-4 mr-2" /> Stato Dati
          </TabsTrigger>
          <TabsTrigger value="audit" className="rounded-none data-[state=active]:bg-white" data-testid="tab-audit">
            <FileText className="w-4 h-4 mr-2" /> Registro Audit
          </TabsTrigger>
          <TabsTrigger value="rbac-audit" className="rounded-none data-[state=active]:bg-white" data-testid="tab-rbac-audit">
            <Lock className="w-4 h-4 mr-2" /> Audit RBAC
          </TabsTrigger>
          <TabsTrigger value="permissions" className="rounded-none data-[state=active]:bg-white" data-testid="tab-permissions">
            <ShieldCheck className="w-4 h-4 mr-2" /> Permessi
          </TabsTrigger>
          <TabsTrigger value="diagnostics" className="rounded-none data-[state=active]:bg-white" data-testid="tab-diagnostics">
            <Activity className="w-4 h-4 mr-2" /> Diagnostica
          </TabsTrigger>
          <TabsTrigger value="reports" className="rounded-none data-[state=active]:bg-white" data-testid="tab-reports">
            <Mail className="w-4 h-4 mr-2" /> Report
          </TabsTrigger>
          <TabsTrigger value="google-reviews" className="rounded-none data-[state=active]:bg-white" data-testid="tab-google-reviews">
            <Star className="w-4 h-4 mr-2" /> Google Reviews
          </TabsTrigger>
          <TabsTrigger value="fresha-aliases" className="rounded-none data-[state=active]:bg-white" data-testid="tab-fresha-aliases">
            <Link2 className="w-4 h-4 mr-2" /> Alias Fresha
          </TabsTrigger>
          <TabsTrigger value="orders-catalog" className="rounded-none data-[state=active]:bg-white" data-testid="tab-orders-catalog">
            <Package className="w-4 h-4 mr-2" /> Catalogo Ordini
          </TabsTrigger>
          <TabsTrigger value="training" className="rounded-none data-[state=active]:bg-white" data-testid="tab-training">
            <GraduationCap className="w-4 h-4 mr-2" /> Formazione
          </TabsTrigger>
        </TabsList>
        <TabsContent value="budget-setup"><BudgetSetup /></TabsContent>
        <TabsContent value="kpi"><KpiSettings /></TabsContent>
        {isOps && <TabsContent value="health-score"><HealthScoreSettings /></TabsContent>}
        <TabsContent value="brands"><BrandsSettings /></TabsContent>
        <TabsContent value="areas"><AreasSettings /></TabsContent>
        {isOps && <TabsContent value="inventory-cat"><InventoryCategoriesSettings /></TabsContent>}
        <TabsContent value="mappings"><AreaManagersEditor /><StoreMappings /></TabsContent>
        <TabsContent value="brand-daily"><BrandDailyConfigPanel /></TabsContent>
        <TabsContent value="imports">
          {/* Embed Import Dati page contents. Lazy: render solo quando il
              tab è attivo (mountOnEnter implicito in Tabs Radix). */}
          <ImportsPage />
        </TabsContent>
        <TabsContent value="data-health"><DataHealthPage /></TabsContent>
        <TabsContent value="audit"><AuditLogPage /></TabsContent>
        <TabsContent value="rbac-audit"><RbacAuditPanel /></TabsContent>
        <TabsContent value="permissions"><PermissionsMatrixPanel /></TabsContent>
        <TabsContent value="diagnostics">
          <DiagnosticsPanel />
          <div className="mt-6">
            <DangerZonePanel />
          </div>
        </TabsContent>
        <TabsContent value="reports"><ReportsPanel /></TabsContent>
        {isOps && <TabsContent value="google-reviews"><GoogleReviewsSettings /></TabsContent>}
        <TabsContent value="fresha-aliases"><FreshaAliasesSettings /></TabsContent>
        <TabsContent value="orders-catalog"><OrdersCatalogSettings /></TabsContent>
        <TabsContent value="training"><TrainingSettingsPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function BudgetSetup() {
  const [config, setConfig] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedCount, setSavedCount] = useState(null);
  const [healing, setHealing] = useState(false);
  const [healMsg, setHealMsg] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    budgetsApi.getSetup().then(r => {
      setConfig(r.data);
      setBaseline(r.data);
    });
  }, []);

  // Diff tra config corrente e baseline (valori effettivamente caricati)
  const changes = React.useMemo(() => {
    if (!config || !baseline) return [];
    const out = [];
    for (const [key, label] of Object.entries(SETUP_FIELD_LABELS)) {
      const oldV = baseline[key];
      const newV = config[key];
      if (oldV === newV || oldV == null || newV === '') continue;
      if (Number(oldV) !== Number(newV)) {
        out.push({ key, label, from: oldV, to: newV });
      }
    }
    return out;
  }, [config, baseline]);

  const impactChangeKeys = changes.map(c => c.key);
  const propagatesToStores = impactChangeKeys.length > 0;

  const doSave = async () => {
    setSaving(true);
    try {
      const res = await budgetsApi.updateSetup(config);
      setConfig(res.data);
      setBaseline(res.data);
      setSavedCount(res.data.recalculated ?? null);
      setSaved(true);
    } catch (err) {
      alert('Errore: ' + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); setConfirmOpen(false); }
  };

  const handleSave = () => {
    if (!propagatesToStores) {
      // Nessuna modifica rispetto al baseline: niente PUT, niente propagazione.
      setSavedCount(0);
      setSaved(true);
      return;
    }
    setConfirmOpen(true);
  };

  // Auto-dismiss del banner di feedback con cleanup corretto
  useEffect(() => {
    if (!saved) return undefined;
    const t = setTimeout(() => { setSaved(false); setSavedCount(null); }, 4000);
    return () => clearTimeout(t);
  }, [saved]);

  const handleHeal = async () => {
    setHealing(true);
    setHealMsg('');
    try {
      const res = await budgetsApi.healSources();
      const n = res.data?.healed ?? 0;
      setHealMsg(n === 0
        ? 'Nessuna source da normalizzare: tutti i badge MANUALE sono già allineati.'
        : `Normalizzate ${n} source: ${n} valore${n === 1 ? '' : 'i'} ora ricondott${n === 1 ? 'o' : 'i'} a "automatico".`);
      setTimeout(() => setHealMsg(''), 5000);
    } catch (err) {
      setHealMsg('Errore: ' + (err.response?.data?.detail || err.message));
    } finally { setHealing(false); }
  };

  const updateField = (key, val) => {
    setConfig(prev => ({ ...prev, [key]: val === '' ? '' : Number(val) }));
  };

  if (!config) return <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" />;

  const svcPct = config.service_revenue_pct || 0;
  const prodPct = config.product_revenue_pct || 0;
  const totalPct = svcPct + prodPct;
  const totalOk = totalPct === 100;

  return (
    <div data-testid="budget-setup">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-xl font-semibold">Setup Budget</h2>
          <p className="text-sm text-slate-500 mt-1">
            Questi valori vengono usati per calcolare automaticamente i KPI budget
            a partire dal valore di vendite totali
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving || !totalOk} className="bg-black text-white hover:bg-slate-900 rounded-none disabled:opacity-50" data-testid="save-budget-setup-btn">
          <Save className="w-4 h-4 mr-2" /> {saved ? 'Salvato!' : saving ? 'Salvataggio...' : 'Salva setup'}
        </Button>
      </div>

      {!totalOk && (
        <div className="mb-4 px-4 py-3 border border-red-300 bg-red-50 text-sm text-red-800" data-testid="setup-pct-error">
          <span className="font-semibold">Salvataggio bloccato:</span> la somma di <span className="font-mono">% Valore Servizi</span> ({svcPct}) e <span className="font-mono">% Valore Prodotti</span> ({prodPct}) deve essere esattamente <span className="font-bold">100%</span>. Attualmente è {totalPct}%. Senza questo vincolo, Ricavo Servizi + Ricavo Prodotti non corrisponderebbe a Vendite Totali.
        </div>
      )}

      {/* Normalizza sources (rimuove badge MANUALE falsi positivi) */}
      <div className="mb-6 flex items-center justify-between bg-amber-50 border border-amber-200 px-4 py-3">
        <div className="text-xs text-amber-900">
          <span className="font-semibold">Normalizza badge MANUALE:</span> ricalcola quali valori di Ore disponibili e Ticket medio sono effettivi override manuali e quali combaciano con il Setup globale. Utile dopo import massivi o cambi di Setup.
          {healMsg && <p className="mt-1 text-amber-800 font-medium">{healMsg}</p>}
        </div>
        <Button
          onClick={handleHeal}
          disabled={healing}
          variant="outline"
          className="rounded-none border-amber-300 bg-white hover:bg-amber-100 shrink-0"
          data-testid="heal-sources-btn"
        >
          <Wand2 className="w-4 h-4 mr-2" /> {healing ? 'Normalizzazione...' : 'Normalizza sources'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Valori Medi */}
        <div className="bg-white border border-slate-200">
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">Valori Medi</span>
          </div>
          <div className="p-5 space-y-4">
            <SetupField label="Valore Medio Servizio" value={config.avg_service_value} suffix="€"
              onChange={(v) => updateField('avg_service_value', v)} testId="setup-avg-service" />
            <SetupField label="Valore Medio Prodotto" value={config.avg_product_value} suffix="€"
              onChange={(v) => updateField('avg_product_value', v)} testId="setup-avg-product" />
          </div>
        </div>

        {/* Ripartizione Budget */}
        <div className="bg-white border border-slate-200">
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">Ripartizione Budget</span>
          </div>
          <div className="p-5 space-y-4">
            <SetupField label="% Valore Servizi" value={config.service_revenue_pct} suffix="%"
              onChange={(v) => updateField('service_revenue_pct', v)} testId="setup-svc-pct" />
            <SetupField label="% Valore Prodotti" value={config.product_revenue_pct} suffix="%"
              onChange={(v) => updateField('product_revenue_pct', v)} testId="setup-prod-pct" />
            <div className={`flex items-center justify-between px-4 py-3 border ${totalOk ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
              <span className="text-sm font-semibold">Controllo Totale %</span>
              <span className={`font-heading text-lg font-bold ${totalOk ? 'text-emerald-700' : 'text-red-700'}`}>{totalPct}%</span>
            </div>
          </div>
        </div>

        {/* Controlli KPI */}
        <div className="bg-white border border-slate-200">
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-600">Controlli Budget KPI</span>
          </div>
          <div className="p-5 space-y-4">
            <SetupField label="Target recensioni/mese per negozio" value={config.review_count}
              onChange={(v) => updateField('review_count', v)} testId="setup-reviews" />
            <SetupField label="% Nuovi clienti" value={config.new_client_pct} suffix="%"
              onChange={(v) => updateField('new_client_pct', v)} testId="setup-new-clients" />
            <SetupField label="Tasso abituali" value={config.returning_client_rate} suffix="%"
              onChange={(v) => updateField('returning_client_rate', v)} testId="setup-returning" />
            <SetupField label="Membership vendute" value={config.memberships_sold}
              onChange={(v) => updateField('memberships_sold', v)} testId="setup-memberships" />
            <SetupField label="% Occupazione" value={config.occupancy_pct} suffix="%"
              onChange={(v) => updateField('occupancy_pct', v)} testId="setup-occupancy" />
            <SetupField label="Ore disponibili (default)" value={config.available_hours}
              onChange={(v) => updateField('available_hours', v)} testId="setup-available-hours"
              hint="Valore di default quando un negozio non ha un override manuale" />
            <SetupField label="Ticket medio (default)" value={config.avg_ticket} suffix="€"
              onChange={(v) => updateField('avg_ticket', v)} testId="setup-avg-ticket"
              hint="Valore di default usato per il Ticket medio budget dei negozi" />
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="bg-white border border-slate-200 mt-6 p-5">
        <h3 className="font-heading text-base font-semibold mb-3">Anteprima calcolo</h3>
        <p className="text-xs text-slate-500 mb-4">Esempio: con vendite totali budget di €20.000 e {config.available_hours}h disponibili</p>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {(() => {
            const total = 20000;
            const sR = total * (svcPct / 100);
            const pR = total * (prodPct / 100);
            const avgS = config.avg_service_value || 1;
            const avgP = config.avg_product_value || 1;
            const ah = config.available_hours || 1;
            const perHour = Math.round((total / ah) * 100) / 100;
            return [
              { l: 'Ricavo servizi', v: `€${Math.round(sR).toLocaleString('it-IT')}` },
              { l: 'Ricavo prodotti', v: `€${Math.round(pR).toLocaleString('it-IT')}` },
              { l: 'N. servizi', v: Math.round(sR / avgS).toLocaleString('it-IT') },
              { l: 'N. prodotti', v: Math.round(pR / avgP).toLocaleString('it-IT') },
              { l: 'Scontrino medio', v: `€${perHour}` },
              { l: '% Occupazione', v: `${config.occupancy_pct}%` },
            ].map(item => (
              <div key={item.l} className="border border-slate-100 p-3">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">{item.l}</span>
                <p className="font-heading text-base font-bold mt-0.5">{item.v}</p>
              </div>
            ));
          })()}
        </div>
      </div>

      {savedCount != null && (
        <div className={`mt-4 px-4 py-3 text-xs border ${savedCount === 0 ? 'bg-slate-50 border-slate-200 text-slate-700' : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`} data-testid="setup-save-feedback">
          {savedCount === 0
            ? <span><span className="font-semibold">Nessuna modifica.</span> Il Setup era già aggiornato.</span>
            : <span><span className="font-semibold">Setup salvato.</span> {savedCount} valori KPI aggiornati sui negozi · gli override manuali (badge MANUALE) sono stati preservati.</span>}
        </div>
      )}

      {/* Conferma propagazione */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="rounded-none" data-testid="setup-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" /> Conferma modifica Setup
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-slate-600 space-y-3">
                <p>Stai per salvare queste modifiche al Setup Budget globale:</p>
                <ul className="space-y-1 border-l-2 border-amber-300 pl-3 py-1 bg-amber-50">
                  {changes.map(c => (
                    <li key={c.key} className="text-slate-800" data-testid={`setup-change-${c.key}`}>
                      <span className="font-semibold">{c.label}:</span>{' '}
                      <span className="text-slate-500 line-through">{c.from}</span>{' → '}
                      <span className="font-semibold text-slate-900">{c.to}</span>
                    </li>
                  ))}
                </ul>
                <p>
                  La modifica verrà <span className="font-semibold">propagata a tutti i negozi</span> su ogni mese dove il valore è automatico.
                  Gli <span className="font-semibold">override manuali</span> (righe con badge MANUALE nel tab Budget) <span className="font-semibold">saranno preservati</span>.
                </p>
                <p className="text-amber-800 text-xs">
                  Suggerimento: se hai appena modificato i dati di un negozio, attendi il salvataggio prima di continuare.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="setup-confirm-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={doSave}
              disabled={saving}
              className="rounded-none bg-black text-white hover:bg-slate-900"
              data-testid="setup-confirm-apply"
            >
              {saving ? 'Salvataggio...' : 'Conferma e propaga'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


/* ==================== BRANDS SETTINGS ==================== */
function BrandsSettings() {
  const [brands, setBrands] = React.useState([]);
  const [name, setName] = React.useState('');
  const [code, setCode] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [toDelete, setToDelete] = React.useState(null);
  const [deleteError, setDeleteError] = React.useState('');

  const load = () => brandsApi.list().then(r => setBrands(r.data));
  React.useEffect(() => { load(); }, []);

  const add = async () => {
    setErr('');
    if (!name.trim()) { setErr('Il nome è obbligatorio'); return; }
    setSaving(true);
    try {
      await brandsApi.create({ name: name.trim(), code: code.trim() });
      setName(''); setCode('');
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleteError('');
    try {
      await brandsApi.remove(toDelete.brand_id);
      setToDelete(null);
      await load();
    } catch (e) {
      setDeleteError(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div data-testid="brands-settings">
      <div className="bg-white border border-slate-200 p-5 mb-4">
        <h3 className="font-heading text-base font-semibold">Aggiungi Brand</h3>
        <p className="text-xs text-slate-500 mt-0.5 mb-4">I brand vengono usati per filtrare dashboard, associare negozi e raggruppare Area Manager.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-xs text-slate-600 font-medium">Nome brand</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Es. Little Italy Barber Shop" className="rounded-none mt-1" data-testid="brand-name-input" />
          </div>
          <div>
            <label className="text-xs text-slate-600 font-medium">Codice (opzionale)</label>
            <Input value={code} onChange={e => setCode(e.target.value)} placeholder="Es. LIBS" className="rounded-none mt-1" data-testid="brand-code-input" />
          </div>
          <Button onClick={add} disabled={saving} className="bg-black text-white hover:bg-slate-900 rounded-none" data-testid="brand-add-btn">
            <Plus className="w-4 h-4 mr-2" /> {saving ? 'Aggiunta...' : 'Aggiungi brand'}
          </Button>
        </div>
        {err && <p className="text-xs text-red-600 mt-2" data-testid="brand-add-error">{err}</p>}
      </div>

      <div className="bg-white border border-slate-200" data-testid="brands-list">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-heading text-base font-semibold">Brand esistenti</h3>
          <span className="text-xs text-slate-500">{brands.length} brand</span>
        </div>
        {brands.length === 0 ? (
          <p className="px-5 py-6 text-sm text-slate-500">Nessun brand configurato.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-5 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Nome</th>
                <th className="text-left px-5 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Codice</th>
                <th className="text-right px-5 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {brands.map(b => (
                <tr key={b.brand_id} className="border-b border-slate-100" data-testid={`brand-row-${b.brand_id}`}>
                  <td className="px-5 py-3 font-medium text-slate-900">{b.name}</td>
                  <td className="px-5 py-3 text-slate-500">{b.code || '—'}</td>
                  <td className="px-5 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setDeleteError(''); setToDelete(b); }}
                      className="rounded-none text-red-600 hover:bg-red-50 hover:text-red-700"
                      data-testid={`brand-delete-btn-${b.brand_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={o => { if (!o) { setToDelete(null); setDeleteError(''); } }}>
        <AlertDialogContent className="rounded-none" data-testid="brand-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina brand "{toDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione non si può annullare. I negozi associati a questo brand bloccheranno l'eliminazione.
              {deleteError && (
                <span className="block mt-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-xs" data-testid="brand-delete-error">{deleteError}</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="brand-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              className="rounded-none bg-red-600 text-white hover:bg-red-700"
              data-testid="brand-delete-confirm"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AreasSettings() {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [error, setError] = useState('');
  const [toDelete, setToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await areasApi.list();
      setAreas(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submitNew = async () => {
    setError('');
    if (!newName.trim()) { setError('Nome area obbligatorio'); return; }
    setCreating(true);
    try {
      await areasApi.create({ name: newName.trim() });
      setNewName('');
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setCreating(false); }
  };

  const startEdit = (a) => { setEditingId(a.area_id); setEditName(a.name); setError(''); };
  const cancelEdit = () => { setEditingId(null); setEditName(''); setError(''); };

  const saveEdit = async (id) => {
    setError('');
    if (!editName.trim()) { setError('Nome area obbligatorio'); return; }
    try {
      await areasApi.update(id, { name: editName.trim() });
      cancelEdit();
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleteError('');
    try {
      await areasApi.delete(toDelete.area_id);
      setToDelete(null);
      await load();
    } catch (e) {
      setDeleteError(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div data-testid="areas-tab" className="space-y-4">
      <div className="bg-white border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="font-heading text-base font-semibold">Aree geografiche</h2>
          <p className="text-xs text-slate-400 mt-1">
            Definisci le aree (es. Nord Est, Nord Ovest, Centro, Sud). Nei negozi potrai sceglierle da un menù a tendina, evitando errori di digitazione.
          </p>
        </div>

        {/* New area form */}
        <div className="px-5 py-4 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitNew(); }}
            placeholder="Es. Nord Est"
            className="rounded-none max-w-xs"
            data-testid="new-area-input"
          />
          <Button
            onClick={submitNew}
            disabled={creating || !newName.trim()}
            className="rounded-none bg-black text-white hover:bg-slate-900 disabled:opacity-40"
            data-testid="add-area-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Aggiungi area
          </Button>
          {error && <p className="text-xs text-red-600 ml-3" data-testid="area-error">{error}</p>}
        </div>

        {loading ? (
          <div className="p-12 text-center"><div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : areas.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <MapPin className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Nessuna area ancora definita</p>
          </div>
        ) : (
          <table className="w-full data-table">
            <thead>
              <tr>
                <th>Nome area</th>
                <th className="text-right">Negozi</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {areas.map(a => {
                const isEditing = editingId === a.area_id;
                return (
                  <tr key={a.area_id} data-testid={`area-row-${a.area_id}`}>
                    <td>
                      {isEditing ? (
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(a.area_id); else if (e.key === 'Escape') cancelEdit(); }}
                          className="rounded-none h-8 text-sm max-w-xs"
                          data-testid={`edit-area-input-${a.area_id}`}
                          autoFocus
                        />
                      ) : (
                        <span className="font-medium">{a.name}</span>
                      )}
                    </td>
                    <td className="text-right text-xs text-slate-500">
                      {a.store_count > 0 ? (
                        <span className="px-2 py-0.5 bg-slate-100 border border-slate-200">
                          {a.store_count} {a.store_count === 1 ? 'negozio' : 'negozi'}
                        </span>
                      ) : (
                        <span className="text-slate-300">0</span>
                      )}
                    </td>
                    <td className="text-right">
                      {isEditing ? (
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => saveEdit(a.area_id)}
                            className="p-1.5 text-emerald-600 hover:bg-slate-100"
                            data-testid={`save-area-${a.area_id}`}
                            title="Salva"
                          >
                            <Save className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="p-1.5 text-slate-400 hover:bg-slate-100"
                            data-testid={`cancel-area-${a.area_id}`}
                            title="Annulla"
                          >
                            <XCircle className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => startEdit(a)}
                            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                            data-testid={`edit-area-${a.area_id}`}
                            title="Rinomina"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => { setToDelete(a); setDeleteError(''); }}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-slate-100"
                            data-testid={`delete-area-${a.area_id}`}
                            title="Elimina"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) { setToDelete(null); setDeleteError(''); } }}>
        <AlertDialogContent className="rounded-none" data-testid="delete-area-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina area "{toDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete?.store_count > 0
                ? `Questa area è usata da ${toDelete.store_count} negozi attivi. Devi prima riassegnarli ad altre aree dalla scheda Mappatura Negozi.`
                : 'L\'area verrà eliminata definitivamente. Operazione non reversibile.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && <p className="text-xs text-red-600" data-testid="delete-area-error">{deleteError}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="delete-area-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={toDelete?.store_count > 0}
              className="rounded-none bg-red-600 hover:bg-red-700 disabled:opacity-40"
              data-testid="delete-area-confirm"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}



function SetupField({ label, value, suffix, onChange, testId, hint }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-700">{label}</span>
        <div className="flex items-center gap-1">
          {suffix === '€' && <span className="text-sm text-slate-400">€</span>}
          <Input
            type="number" step="any"
            className="w-24 rounded-none text-right font-mono h-8 text-sm"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
          />
          {suffix === '%' && <span className="text-sm text-slate-400">%</span>}
        </div>
      </div>
      {hint && <p className="text-[10px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function KpiSettings() {
  const [defs, setDefs] = useState([]);
  const [thresholds, setThresholds] = useState([]);
  useEffect(() => {
    Promise.all([kpiApi.definitions(), kpiApi.thresholds()])
      .then(([dRes, tRes]) => { setDefs(dRes.data); setThresholds(tRes.data); });
  }, []);
  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200" data-testid="kpi-definitions-table">
        <div className="px-5 py-4 border-b border-slate-200"><h2 className="font-heading text-base font-semibold">Definizioni KPI</h2></div>
        <table className="w-full data-table">
          <thead><tr><th>KPI</th><th>Chiave</th><th>Tipo</th><th>Formato</th><th>Fonte</th><th>Soglia</th></tr></thead>
          <tbody>
            {defs.map(d => (
              <tr key={d.kpi_id} data-testid={`kpi-def-${d.key}`}>
                <td className="font-medium">{d.label}</td>
                <td className="font-mono text-xs text-slate-500">{d.key}</td>
                <td><span className="text-xs px-1.5 py-0.5 bg-slate-50 border">{d.type}</span></td>
                <td className="text-xs text-slate-500">{d.format}</td>
                <td><span className="text-xs px-1.5 py-0.5 bg-slate-50 border">{d.source_type}</span></td>
                <td className="text-xs text-slate-500">{d.threshold_type}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="bg-white border border-slate-200" data-testid="threshold-rules">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="font-heading text-base font-semibold">Regole soglie semafori</h2>
          <p className="text-xs text-slate-500 mt-1">
            Modifica solo le soglie <strong>percentuali</strong>. Anche i KPI con soglia assoluta (€, numeri) vengono valutati convertendo la varianza in % del budget e applicando queste stesse soglie, in proporzione.
          </p>
        </div>
        <ThresholdEditor />
      </div>
    </div>
  );
}

function ThresholdEditor() {
  const [rules, setRules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { kpiApi.thresholds().then(r => setRules(r.data)); }, []);

  const pctRule = rules.find(r => r.threshold_type === 'percentage');
  const updatePct = (field, val) => setRules(prev => prev.map(r => r.threshold_type === 'percentage' ? { ...r, [field]: val === '' ? '' : Number(val) } : r));

  const save = async () => {
    if (!pctRule) return;
    setSaving(true);
    try {
      const payload = {
        green_min: Number(pctRule.green_min),
        yellow_min: Number(pctRule.yellow_min),
        yellow_max: Number(pctRule.yellow_max),
        red_max: Number(pctRule.red_max),
      };
      await kpiApi.updateThreshold(pctRule.rule_id, payload);
      // Live-update del cache soglie lato UI: tooltip, Hero chip, bar chart e
      // ogni altro consumer di lib/semaphore riflettono le nuove bande all'istante,
      // senza bisogno di refresh della pagina.
      setSemaphoreThresholds({ green_min: payload.green_min, red_max: payload.red_max });
      setSaved(true); setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  if (!pctRule) return <div className="p-5 text-sm text-slate-400">Caricamento...</div>;

  return (
    <div className="p-5 space-y-4" data-testid="threshold-editor">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <ThresholdField label="Verde (min %)" value={pctRule.green_min} onChange={(v) => updatePct('green_min', v)} color="text-emerald-700" testId="thr-green" />
        <ThresholdField label="Giallo (min %)" value={pctRule.yellow_min} onChange={(v) => updatePct('yellow_min', v)} color="text-amber-700" testId="thr-ymin" />
        <ThresholdField label="Giallo (max %)" value={pctRule.yellow_max} onChange={(v) => updatePct('yellow_max', v)} color="text-amber-700" testId="thr-ymax" />
        <ThresholdField label="Rosso (max %)" value={pctRule.red_max} onChange={(v) => updatePct('red_max', v)} color="text-red-700" testId="thr-red" />
      </div>
      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
        <p className="text-xs text-slate-500">
          Esempio: se Verde ≥ {pctRule.green_min}% e Rosso ≤ {pctRule.red_max}%, un budget di €10.000 → verde a partire da +€{Math.round(10000 * pctRule.green_min / 100)} e rosso sotto €{Math.round(10000 * pctRule.red_max / 100)}.
        </p>
        <Button onClick={save} disabled={saving} className="bg-black text-white hover:bg-slate-900 rounded-none" data-testid="save-thresholds-btn">
          {saved ? 'Salvato!' : saving ? 'Salvataggio...' : 'Salva soglie'}
        </Button>
      </div>
      {saved && (
        <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 text-xs text-emerald-900" data-testid="thresholds-saved-feedback">
          <span className="font-semibold">Soglie aggiornate.</span> Tooltip, chip Hero e grafici usano già le nuove bande — nessun refresh necessario.
        </div>
      )}
    </div>
  );
}

function ThresholdField({ label, value, onChange, color, testId }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 block mb-1">{label}</label>
      <Input type="number" step="any" value={value ?? ''} onChange={(e) => onChange(e.target.value)}
        className={`rounded-none font-mono text-right ${color}`} data-testid={testId} />
    </div>
  );
}

function StoreMappings() {
  const [mappings, setMappings] = useState([]);
  const [brands, setBrands] = useState([]);
  const [areaManagers, setAreaManagers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingStoreId, setEditingStoreId] = useState(null);
  const [form, setForm] = useState({ display_name: '', code: '', brand_id: '', area_manager_id: '', area: '', fresha_reference: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toToggle, setToToggle] = useState(null);  // {store_id, display_name, toActive: bool}
  const [toggleError, setToggleError] = useState('');

  const load = async () => {
    const [m, b, a, ar] = await Promise.all([
      storeMappingsApi.list(), brandsApi.list(), areaManagersApi.list(), areasApi.list(),
    ]);
    setMappings(m.data);
    setBrands(b.data);
    setAreaManagers(a.data);
    setAreas(ar.data);
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditingStoreId(null);
    setForm({ display_name: '', code: '', brand_id: '', area_manager_id: '', area: '', fresha_reference: '' });
    setError('');
    setShowForm(true);
  };

  const openEdit = async (mapping) => {
    setError('');
    try {
      const r = await storesApi.get(mapping.store_id);
      const s = r.data;
      const amIds = s.area_manager_ids || (s.area_manager_id ? [s.area_manager_id] : []);
      setEditingStoreId(s.store_id);
      setForm({
        display_name: s.display_name || '',
        code: s.code || '',
        brand_id: s.brand_id || '',
        area_manager_id: amIds[0] || '',
        area: s.area || '',
        fresha_reference: s.fresha_reference || mapping.fresha_reference || '',
      });
      setShowForm(true);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingStoreId(null);
    setError('');
  };

  const submit = async () => {
    setError('');
    if (!form.display_name.trim()) { setError('Il nome negozio è obbligatorio'); return; }
    setSaving(true);
    try {
      const payload = {
        display_name: form.display_name.trim(),
        code: form.code.trim(),
        brand_id: form.brand_id || '',
        area: form.area.trim(),
        fresha_reference: form.fresha_reference.trim(),
        area_manager_ids: form.area_manager_id ? [form.area_manager_id] : [],
      };
      if (editingStoreId) {
        await storesApi.update(editingStoreId, payload);
      } else {
        await storesApi.create(payload);
      }
      closeForm();
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmToggle = async () => {
    if (!toToggle) return;
    setToggleError('');
    try {
      await storesApi.update(toToggle.store_id, { active: toToggle.toActive });
      setToToggle(null);
      await load();
    } catch (e) {
      setToggleError(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div data-testid="store-mappings-tab">
      <div className="bg-white border border-slate-200 mb-4" data-testid="store-mappings-table">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div>
            <h2 className="font-heading text-base font-semibold">Mappatura negozi Fresha</h2>
            <p className="text-xs text-slate-400 mt-1">
              {mappings.length} associazioni · {mappings.filter(m => m.store_active).length} attive · {mappings.filter(m => !m.store_active).length} disattivate
            </p>
          </div>
          <Button
            onClick={() => showForm ? closeForm() : openAdd()}
            className="bg-black text-white hover:bg-slate-900 rounded-none"
            data-testid="add-store-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> {showForm ? 'Chiudi' : 'Aggiungi negozio'}
          </Button>
        </div>

        {showForm && (
          <div className="bg-slate-50 border-b border-slate-200 p-5" data-testid="add-store-form">
            <h3 className="font-heading text-sm font-semibold mb-3 text-slate-700">
              {editingStoreId ? 'Modifica negozio' : 'Nuovo negozio'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-600 font-medium">Nome negozio *</label>
                <Input value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} placeholder="Es. Milano Duomo" className="rounded-none mt-1" data-testid="store-name-input" />
              </div>
              <div>
                <label className="text-xs text-slate-600 font-medium">Codice</label>
                <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="Es. MIL" className="rounded-none mt-1" data-testid="store-code-input" />
              </div>
              <div>
                <label className="text-xs text-slate-600 font-medium">Brand</label>
                <Select value={form.brand_id || 'none'} onValueChange={v => setForm({ ...form, brand_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="store-brand-select"><SelectValue placeholder="Seleziona brand" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Nessuno —</SelectItem>
                    {brands.map(b => <SelectItem key={b.brand_id} value={b.brand_id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-600 font-medium">Area Manager</label>
                <Select value={form.area_manager_id || 'none'} onValueChange={v => setForm({ ...form, area_manager_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="store-am-select"><SelectValue placeholder="Seleziona Area Manager" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Nessuno —</SelectItem>
                    {areaManagers.map(a => <SelectItem key={a.area_manager_id} value={a.area_manager_id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-600 font-medium">Area geografica</label>
                <Select
                  value={form.area || 'none'}
                  onValueChange={v => setForm({ ...form, area: v === 'none' ? '' : v })}
                >
                  <SelectTrigger className="rounded-none mt-1" data-testid="store-area-select">
                    <SelectValue placeholder="Seleziona area" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Nessuna —</SelectItem>
                    {areas.map(a => (
                      <SelectItem key={a.area_id} value={a.name} data-testid={`area-option-${a.area_id}`}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {areas.length === 0 && (
                  <p className="text-[10px] text-slate-500 mt-1">
                    Nessuna area definita. Vai nella scheda <strong>Aree</strong> per crearle.
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs text-slate-600 font-medium">Riferimento Fresha</label>
                <Input value={form.fresha_reference} onChange={e => setForm({ ...form, fresha_reference: e.target.value })} placeholder="Es. Milano - cc Duomo | Little Italy Barbershop" className="rounded-none mt-1" data-testid="store-fresha-ref-input" />
                <p className="text-[10px] text-slate-500 mt-1">Serve per associare le righe del file import Fresha al negozio. Puoi aggiungerlo in seguito.</p>
              </div>
            </div>
            {error && <p className="text-xs text-red-600 mt-3" data-testid="add-store-error">{error}</p>}
            <div className="flex gap-2 mt-4">
              <Button onClick={submit} disabled={saving} className="bg-black text-white hover:bg-slate-900 rounded-none" data-testid="add-store-save">
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Salvataggio...' : (editingStoreId ? 'Aggiorna negozio' : 'Salva negozio')}
              </Button>
              <Button variant="outline" onClick={closeForm} className="rounded-none" data-testid="add-store-cancel">
                Annulla
              </Button>
            </div>
          </div>
        )}

        <table className="w-full data-table">
          <thead><tr><th>Negozio interno</th><th>Riferimento Fresha</th><th>Stato</th><th className="w-20 text-right pr-5">Azioni</th></tr></thead>
          <tbody>
            {mappings.length === 0 ? (
              <tr><td colSpan={4} className="px-5 py-6 text-center text-sm text-slate-500">Nessuna mappatura. Clicca "Aggiungi negozio" per iniziare.</td></tr>
            ) : mappings.map(m => (
              <tr key={m.mapping_id} data-testid={`mapping-row-${m.mapping_id}`} className={`${editingStoreId === m.store_id ? 'bg-amber-50' : ''} ${!m.store_active ? 'opacity-60' : ''}`}>
                <td className="font-medium">
                  {m.store_name}
                  {!m.store_active && <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold bg-slate-200 text-slate-600 px-1.5 py-0.5">Disattivato</span>}
                </td>
                <td className="text-sm text-slate-600">{m.fresha_reference || <span className="text-slate-400">—</span>}</td>
                <td><div className={`w-2 h-2 rounded-full ${m.store_active ? 'bg-emerald-500' : 'bg-slate-300'}`} /></td>
                <td className="text-right pr-3">
                  <div className="inline-flex gap-0.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(m)}
                      className="h-8 w-8 p-0 text-slate-500 hover:text-black"
                      data-testid={`mapping-edit-${m.mapping_id}`}
                      title="Modifica"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setToggleError(''); setToToggle({ store_id: m.store_id, display_name: m.store_name, toActive: !m.store_active }); }}
                      className={`h-8 w-8 p-0 ${m.store_active ? 'text-slate-500 hover:text-red-700' : 'text-slate-500 hover:text-emerald-700'}`}
                      data-testid={`mapping-toggle-${m.mapping_id}`}
                      title={m.store_active ? 'Disattiva' : 'Riattiva'}
                    >
                      {m.store_active ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!toToggle} onOpenChange={(v) => { if (!v) { setToToggle(null); setToggleError(''); } }}>
        <AlertDialogContent className="rounded-none" data-testid="store-toggle-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {toToggle?.toActive
                ? <><PowerOff className="w-5 h-5 text-emerald-700" /> Riattiva negozio</>
                : <><Power className="w-5 h-5 text-red-700" /> Disattiva negozio</>}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-slate-600 space-y-2">
                {toToggle?.toActive ? (
                  <p>Vuoi riattivare <strong>{toToggle?.display_name}</strong>? Il negozio tornerà visibile in Panoramica, Dashboard Area e filtri.</p>
                ) : (
                  <>
                    <p>Vuoi disattivare <strong>{toToggle?.display_name}</strong>?</p>
                    <p className="text-xs text-slate-500">Il negozio <strong>non verrà eliminato</strong>: storico KPI, budget e costi restano in database. Non apparirà però in Panoramica, Dashboard Area e filtri finché non lo riattivi.</p>
                  </>
                )}
                {toggleError && <p className="text-xs text-red-600">{toggleError}</p>}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="store-toggle-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmToggle}
              className={`rounded-none text-white ${toToggle?.toActive ? 'bg-emerald-700 hover:bg-emerald-800' : 'bg-red-700 hover:bg-red-800'}`}
              data-testid="store-toggle-confirm"
            >
              {toToggle?.toActive ? 'Riattiva' : 'Disattiva'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DiagnosticsPanel() {
  const [health, setHealth] = useState(null);
  const [ready, setReady] = useState(null);
  const [readyError, setReadyError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  const refresh = async () => {
    setLoading(true); setError(null); setReadyError(null);
    try {
      const [h, r] = await Promise.allSettled([healthApi.status(), healthApi.ready()]);
      if (h.status === 'fulfilled') setHealth(h.value.data);
      else setError(h.reason?.response?.data?.detail || h.reason?.message || 'Errore ignoto');
      if (r.status === 'fulfilled') { setReady(r.value.data); setReadyError(null); }
      else { setReady(null); setReadyError(r.reason?.response?.data?.detail || r.reason?.message || 'Errore ignoto'); }
      setLastFetch(new Date());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  return (
    <div className="bg-white border border-slate-200 p-6" data-testid="diagnostics-panel">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="font-heading text-lg font-semibold tracking-tight">Diagnostica Sistema</h2>
          <p className="text-sm text-slate-500 mt-1">
            Stato del backend, indici MongoDB, ultima importazione.
            {lastFetch && <span className="text-xs text-slate-400 ml-2">Aggiornato alle {lastFetch.toLocaleTimeString('it-IT')}</span>}
          </p>
        </div>
        <Button onClick={refresh} disabled={loading} variant="outline" data-testid="diagnostics-refresh" className="rounded-none">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Ricarica
        </Button>
      </div>

      {error ? (
        <div className="border border-red-200 bg-red-50 p-4 flex items-center gap-3" data-testid="diagnostics-error">
          <XCircle className="w-5 h-5 text-red-700" />
          <div>
            <p className="font-semibold text-red-900">Backend non raggiungibile</p>
            <p className="text-sm text-red-700">{String(error)}</p>
          </div>
        </div>
      ) : health ? (
        <div className="space-y-6">
          {/* Status cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatusCard
              label="Stato DB"
              value={health.db === 'ok' ? 'Operativo' : 'Errore'}
              ok={health.db === 'ok'}
              testId="diag-db"
            />
            <StatusCard
              label="Readiness"
              value={ready?.status === 'ready' ? 'Pronto' : 'Non pronto'}
              ok={ready?.status === 'ready'}
              hint={ready?.last_import_age_days != null ? `Ultimo import ${ready.last_import_age_days}gg fa` : readyError}
              testId="diag-ready"
            />
            <InfoCard label="Indici MongoDB" value={health.indexes_total} hint="Indici custom totali" testId="diag-indexes" />
            <InfoCard label="Uptime" value={fmtUptime(health.uptime_seconds)} hint={new Date(health.timestamp).toLocaleString('it-IT')} testId="diag-uptime" />
          </div>

          {/* Totals */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <InfoCard label="Negozi attivi" value={health.stores_active} testId="diag-stores" />
            <InfoCard label="Valori KPI in DB" value={Number(health.kpi_values_total).toLocaleString('it-IT')} testId="diag-kpi-count" />
            <InfoCard
              label="Ultima importazione"
              value={health.last_import ? `${health.last_import.filename}` : '—'}
              hint={health.last_import ? `${fmtImportPeriod(health.last_import)} · ${fmtDate(health.last_import.confirmed_at)}` : 'Nessuna'}
              testId="diag-last-import"
            />
          </div>

          {/* Index detail */}
          <div>
            <h3 className="font-heading text-sm font-semibold mb-2 uppercase tracking-wider text-slate-500">Indici per collezione</h3>
            <div className="border border-slate-200">
              <table className="w-full data-table text-sm">
                <thead>
                  <tr>
                    <th>Collezione</th>
                    <th className="text-right">Indici custom</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(health.indexes).map(([coll, count]) => (
                    <tr key={coll} data-testid={`diag-idx-${coll}`}>
                      <td className="font-mono text-xs">{coll}</td>
                      <td className="num">
                        {count == null ? <span className="text-slate-400">—</span>
                          : count === 0 ? <span className="text-amber-700">{count}</span>
                          : <span className="text-slate-900">{count}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="py-12 flex justify-center">
          <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Strumento di diagnosi accesso utente — solo OPS (2026-06-09).
          Risolve i casi "Accesso negato": l'OPS digita l'email
          dell'utente e ottiene un verdetto + cosa fare. */}
      <AuthDiagnoseTool />
    </div>
  );
}

function AuthDiagnoseTool() {
  const [email, setEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError(null);
    setResult(null);
    if (!email.trim() || !email.includes('@')) {
      setError("Inserisci un'email valida");
      return;
    }
    setLoading(true);
    try {
      const apiMod = await import('../lib/api');
      const apiClient = apiMod.default;
      const { data } = await apiClient.get('/admin/auth-diagnose', { params: { email: email.trim() } });
      setResult(data);
    } catch (e2) {
      setError(e2.response?.data?.detail || e2.message || 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  };

  const verdictTone = (verdict) => {
    if (!verdict) return 'border-slate-200 bg-slate-50 text-slate-700';
    if (verdict.startsWith('FOUND_ACTIVE')) return 'border-emerald-200 bg-emerald-50 text-emerald-900';
    if (verdict.startsWith('FOUND_INVITED')) return 'border-amber-200 bg-amber-50 text-amber-900';
    if (verdict.startsWith('FOUND_BUT_SUSPENDED')) return 'border-red-200 bg-red-50 text-red-900';
    if (verdict.startsWith('STAFF_BUT_NO_USER')) return 'border-blue-200 bg-blue-50 text-blue-900';
    if (verdict.startsWith('TYPO_OR_DOMAIN_MISMATCH')) return 'border-amber-200 bg-amber-50 text-amber-900';
    return 'border-red-200 bg-red-50 text-red-900';
  };

  return (
    <div className="mt-8 border-t border-slate-200 pt-6" data-testid="auth-diagnose-tool">
      <h3 className="font-heading text-base font-semibold tracking-tight mb-1">Diagnosi accesso utente</h3>
      <p className="text-sm text-slate-500 mb-4">
        Quando qualcuno ti segnala &quot;Accesso negato&quot;, incolla qui la sua email per capire subito cosa è successo e cosa fare.
      </p>

      <form onSubmit={handleSubmit} className="flex flex-wrap gap-2 items-center">
        <input
          type="email"
          placeholder="es. giuseppe@littleitalybarbershop.it"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1 min-w-[260px] border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
          data-testid="auth-diagnose-input"
        />
        <Button type="submit" disabled={loading} className="rounded-none" data-testid="auth-diagnose-submit">
          {loading ? 'Diagnosi…' : 'Diagnosi'}
        </Button>
      </form>

      {error && (
        <div className="mt-4 border border-red-200 bg-red-50 p-3 text-sm text-red-900" data-testid="auth-diagnose-error">
          {String(error)}
        </div>
      )}

      {result && (
        <div className="mt-4 space-y-4" data-testid="auth-diagnose-result">
          <div className={`border p-4 ${verdictTone(result.verdict)}`}>
            <p className="text-[10px] uppercase tracking-wider font-bold mb-1">Verdetto</p>
            <p className="text-sm font-medium leading-relaxed">{result.verdict}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="border border-slate-200 p-3">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Match esatto in Utenti</p>
              {result.user_exact_match ? (
                <ul className="space-y-1 text-slate-800">
                  <li><span className="text-slate-500">Email:</span> {result.user_exact_match.email}</li>
                  <li><span className="text-slate-500">Nome:</span> {result.user_exact_match.name || '—'}</li>
                  <li><span className="text-slate-500">Ruolo:</span> {result.user_exact_match.role}</li>
                  <li><span className="text-slate-500">Stato:</span> {result.user_exact_match.status || '—'}</li>
                  <li><span className="text-slate-500">User ID:</span> <code className="text-xs">{result.user_exact_match.user_id}</code></li>
                </ul>
              ) : (
                <p className="text-slate-400 text-xs italic">Nessun match in Utenti.</p>
              )}
            </div>
            <div className="border border-slate-200 p-3">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Match esatto in Staff</p>
              {result.staff_exact_match ? (
                <ul className="space-y-1 text-slate-800">
                  <li><span className="text-slate-500">Nome:</span> {result.staff_exact_match.first_name} {result.staff_exact_match.last_name}</li>
                  <li><span className="text-slate-500">Email lavoro:</span> {result.staff_exact_match.email_work || '—'}</li>
                  <li><span className="text-slate-500">Email personale:</span> {result.staff_exact_match.email_personal || '—'}</li>
                  <li><span className="text-slate-500">Store:</span> {result.staff_exact_match.store_id || '—'}</li>
                  <li><span className="text-slate-500">User linkato:</span> {result.staff_exact_match.user_id || <span className="text-red-700 font-semibold">NON LINKATO</span>}</li>
                </ul>
              ) : (
                <p className="text-slate-400 text-xs italic">Nessun match in Staff.</p>
              )}
            </div>
          </div>

          {(result.users_near_matches?.length > 0 || result.staff_near_matches?.length > 0) && (
            <div className="border border-slate-200 p-3 text-sm">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Account simili in DB</p>
              {result.users_near_matches?.length > 0 && (
                <ul className="text-slate-800 mb-2 list-disc pl-5">
                  {result.users_near_matches.map((u) => (
                    <li key={u.user_id}>
                      <span className="font-medium">{u.email}</span> · {u.role} · {u.status || '—'}
                    </li>
                  ))}
                </ul>
              )}
              {result.staff_near_matches?.length > 0 && (
                <ul className="text-slate-800 list-disc pl-5">
                  {result.staff_near_matches.map((s) => (
                    <li key={s.staff_id}>
                      <span className="font-medium">{s.first_name} {s.last_name}</span> · lav: {s.email_work || '—'} · pers: {s.email_personal || '—'}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {result.recent_login_denials?.length > 0 && (
            <div className="border border-slate-200 p-3 text-sm">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Ultimi accessi negati</p>
              <ul className="text-slate-800 space-y-1">
                {result.recent_login_denials.map((d) => (
                  <li key={`${d.created_at}-${d.meta?.google_email || d.actor_email || ''}`} className="text-xs">
                    <span className="text-slate-500">{new Date(d.created_at).toLocaleString('it-IT')}</span>
                    {' · '}
                    Google ha passato: <code>{d.meta?.google_email || d.actor_email || '?'}</code>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}



function StatusCard({ label, value, ok, hint, testId }) {
  const Icon = ok ? CheckCircle2 : XCircle;
  return (
    <div className={`border p-4 ${ok ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${ok ? 'text-emerald-700' : 'text-red-700'}`} />
        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</span>
      </div>
      <p className={`font-heading text-xl font-bold ${ok ? 'text-emerald-900' : 'text-red-900'}`}>{value}</p>
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

function InfoCard({ label, value, hint, testId }) {
  return (
    <div className="border border-slate-200 p-4" data-testid={testId}>
      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</span>
      <p className="font-heading text-xl font-bold mt-1 text-slate-900">{value ?? '—'}</p>
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

function fmtUptime(seconds) {
  if (seconds == null) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}g`;
}

function fmtImportPeriod(imp) {
  const s = imp.scenario || '';
  if (imp.month) return `${s} ${String(imp.month).padStart(2, '0')}/${imp.year}`;
  return `${s} ${imp.year}`;
}

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('it-IT'); }
  catch { return iso; }
}



function ReportsPanel() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [lastSend, setLastSend] = useState(null);
  const [error, setError] = useState('');

  const loadPreview = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await reportsApi.previewWeeklyGaps();
      setPreview(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const handleSendNow = async () => {
    if (!window.confirm('Inviare ora il report settimanale a tutti gli OPS attivi?')) return;
    setSending(true);
    setError('');
    try {
      const r = await reportsApi.sendWeeklyGaps();
      setLastSend(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSending(false);
    }
  };

  const counts = preview ? {
    costs: preview.missing_costs?.length || 0,
    budget: preview.missing_budget?.length || 0,
    late: preview.late_imports?.length || 0,
  } : null;
  const gapTotal = counts ? counts.costs + counts.budget + counts.late : 0;

  return (
    <div className="max-w-4xl space-y-6" data-testid="reports-panel">
      <div className="bg-white border border-slate-200 p-6">
        <div className="flex items-start justify-between gap-6 mb-5">
          <div>
            <h2 className="font-heading text-base font-semibold">Report settimanale dei gap</h2>
            <p className="text-sm text-slate-500 mt-1 max-w-xl">
              Ogni <strong>lunedì alle 08:00 (Europa/Roma)</strong> tutti gli Operations Manager
              attivi ricevono via email un riepilogo dei negozi con dati mancanti o import in
              ritardo. Puoi usare il pulsante qui sotto per inviare manualmente un'anteprima
              ora stesso.
            </p>
          </div>
          <Button
            onClick={handleSendNow}
            disabled={sending || loading}
            className="bg-black text-white hover:bg-slate-900 rounded-none flex-shrink-0"
            data-testid="send-weekly-report-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            {sending ? 'Invio in corso…' : 'Invia adesso'}
          </Button>
        </div>

        {loading ? (
          <div className="py-8 text-sm text-slate-400 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Caricamento anteprima…
          </div>
        ) : error ? (
          <div className="p-4 bg-red-50 border border-red-200 text-sm text-red-700" data-testid="reports-error">
            {error}
          </div>
        ) : preview ? (
          <>
            <div className="grid grid-cols-3 gap-3 mb-5">
              <StatTile label="Costi mancanti" count={counts.costs} color="rose" />
              <StatTile label="Budget mancante" count={counts.budget} color="amber" />
              <StatTile label="Import in ritardo (4+ gg)" count={counts.late} color="red" />
            </div>
            {gapTotal === 0 ? (
              <div className="p-4 bg-emerald-50 border border-emerald-200 text-sm text-emerald-700" data-testid="no-gaps-banner">
                ✓ Nessun gap rilevato — il report settimanale sarà comunque inviato lunedì mattina.
              </div>
            ) : (
              <div className="space-y-4">
                <GapList title="Costi mancanti" items={preview.missing_costs || []} testId="gap-list-costs" />
                <GapList title="Budget mancante" items={preview.missing_budget || []} testId="gap-list-budget" />
                <GapList title="Import giornaliero in ritardo" items={preview.late_imports || []} testId="gap-list-late" />
              </div>
            )}
          </>
        ) : null}

        {lastSend && (
          <div className="mt-5 p-4 bg-slate-50 border border-slate-200 text-sm" data-testid="last-send-banner">
            <p className="font-semibold text-slate-900">
              Report inviato a {lastSend.recipients?.length || 0} destinatario
              {lastSend.recipients?.length === 1 ? '' : 'i'}
            </p>
            <p className="text-slate-500 mt-1">
              {lastSend.gaps_total} gap segnalati · {new Date(lastSend.sent_at).toLocaleString('it-IT')}
            </p>
            {lastSend.delivery?.some(d => d.status === 'failed') && (
              <p className="text-red-600 mt-2">
                Alcune email hanno fallito — controlla l'audit log per i dettagli.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, count, color }) {
  const palette = {
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    red: 'bg-red-50 border-red-200 text-red-700',
  }[color] || 'bg-slate-50 border-slate-200 text-slate-700';
  return (
    <div className={`border p-4 ${palette}`}>
      <p className="text-xs uppercase tracking-wider font-semibold opacity-80">{label}</p>
      <p className="font-heading text-3xl font-bold mt-2">{count}</p>
    </div>
  );
}

function GapList({ title, items, testId }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="bg-slate-50 border border-slate-200 p-4" data-testid={testId}>
      <p className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2">{title} · {items.length}</p>
      <ul className="text-sm text-slate-700 space-y-1">
        {items.map((it, i) => <li key={`gap-${it}-${i}`} className="font-mono text-[13px]">{it}</li>)}
      </ul>
    </div>
  );
}


const RESET_CONFIRMATION = 'RESET_PRODUCTION_NOW';

/**
 * Danger Zone — wipes business data and keeps only the 2 OPS users + org tree.
 * Designed for one-shot use after deploy to bring production into a clean
 * state matching staging. Triple-guarded:
 *   - Type-the-phrase confirmation
 *   - AlertDialog with explicit warning
 *   - Backend requires the same phrase echoed in the body
 */
function DangerZonePanel() {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const canSubmit = typed === RESET_CONFIRMATION && !busy;

  const handleReset = async () => {
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const r = await adminApi.hardReset(RESET_CONFIRMATION);
      setResult(r.data);
      setOpen(false);
      setTyped('');
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-white border-2 border-red-500 p-6"
      data-testid="danger-zone-panel"
    >
      <h2 className="font-heading text-lg font-semibold tracking-tight text-red-700 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5" /> Danger Zone
      </h2>
      <p className="text-sm text-slate-600 mt-2 max-w-2xl">
        Cancella i dati <strong>operativi</strong> (KPI mensili e giornalieri, import,
        costi negozio, note, AI insights, override manuali, budget) e tutti gli utenti
        tranne gli OPS{' '}
        <code className="text-xs bg-slate-100 px-1">ops@littleitalybarbershop.it</code> e{' '}
        <code className="text-xs bg-slate-100 px-1">info@littleitalybarbershop.it</code>.
        L'albero organizzativo (brand / aree / area manager / negozi / mapping Fresha) e la
        configurazione di sistema (KPI definitions, soglie, budget setup, categorie costi)
        rimangono intatti.
      </p>
      <p className="text-xs text-slate-600 mt-2 max-w-2xl">
        <strong>Vengono preservati</strong> anche: registro audit, sessioni utente,
        idee del backlog (incluso commenti) e log degli alert email. Questi non sono
        considerati dati operativi e mantenerli garantisce tracciabilità + continuità
        della community/feedback dopo il reset.
      </p>
      <p className="text-xs text-red-600 mt-2 font-semibold uppercase tracking-wider">
        Azione irreversibile. Esegui solo dopo aver verificato di essere nell'ambiente corretto.
      </p>

      <Button
        onClick={() => setOpen(true)}
        className="mt-4 bg-red-600 hover:bg-red-700 text-white rounded-none"
        data-testid="danger-zone-trigger"
      >
        <Trash2 className="w-4 h-4 mr-2" /> Esegui hard-reset
      </Button>

      {result && (
        <div
          className="mt-5 p-4 bg-emerald-50 border border-emerald-200 text-sm"
          data-testid="danger-zone-result"
        >
          <p className="font-semibold text-emerald-800">
            Reset completato — {result.total_wiped} documenti cancellati
          </p>
          <p className="text-emerald-700 mt-1 text-xs">
            Eseguito da {result.executed_by} il{' '}
            {new Date(result.executed_at).toLocaleString('it-IT')}
          </p>
          <details className="mt-2 text-xs">
            <summary className="cursor-pointer text-emerald-700">Dettagli per collezione</summary>
            <ul className="mt-2 space-y-0.5 font-mono text-[11px]">
              {Object.entries(result.wiped || {}).map(([k, v]) => (
                <li key={k}>{k}: -{v}</li>
              ))}
            </ul>
          </details>
        </div>
      )}

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="danger-zone-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Conferma hard-reset
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                Stai per cancellare permanentemente i dati <strong>operativi</strong>
                {' '}(KPI, costi, import, note, budget) e quasi tutti gli utenti
                dell'ambiente in cui sei loggato.
              </span>
              <span className="block text-slate-600">
                Audit log, sessioni, idee del backlog, commenti e alert log
                <strong> non vengono toccati</strong>.
              </span>
              <span className="block">
                Per procedere, scrivi qui sotto la frase di conferma esatta:
              </span>
              <code className="block bg-slate-100 px-2 py-1 mt-1 text-sm font-bold">
                {RESET_CONFIRMATION}
              </code>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Scrivi la frase di conferma…"
            className="font-mono"
            autoFocus
            data-testid="danger-zone-input"
          />
          {error && (
            <p className="text-sm text-red-600" data-testid="danger-zone-error">
              {error}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="danger-zone-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReset}
              disabled={!canSubmit}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="danger-zone-confirm"
            >
              {busy ? 'Reset in corso…' : 'Sì, cancella tutto'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HealthScoreSettings — pannello OPS per ritarare pesi e soglie dello Health
// Score senza dover toccare il codice. Tutti i parametri sono persistiti su
// `health_score_config` (collection Mongo, singolo documento `default`). Il
// frontend ha un mirror runtime in `lib/health.js` aggiornato dopo il save
// così Area/Store/Overview dashboards riflettono subito la nuova taratura
// senza F5 — purché ricarichino i dati delle dashboard.
// ---------------------------------------------------------------------------
function HealthScoreSettings() {
  const [cfg, setCfg] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');
  const [resetOpen, setResetOpen] = useState(false);

  useEffect(() => {
    healthScoreConfigApi.get().then(r => {
      setCfg(r.data);
      setBaseline(r.data);
    });
  }, []);

  const isDirty = React.useMemo(() => {
    if (!cfg || !baseline) return false;
    return JSON.stringify(cfg.semaphore_weights) !== JSON.stringify(baseline.semaphore_weights)
      || JSON.stringify(cfg.band_thresholds) !== JSON.stringify(baseline.band_thresholds)
      || JSON.stringify(cfg.kpi_importance) !== JSON.stringify(baseline.kpi_importance);
  }, [cfg, baseline]);

  const handleNum = (path) => (e) => {
    const raw = e.target.value;
    const v = raw === '' ? '' : Number(raw);
    setCfg(prev => {
      const next = { ...prev };
      const [section, key] = path.split('.');
      next[section] = { ...next[section], [key]: v };
      return next;
    });
    setSaved(false); setErr('');
  };

  const validate = () => {
    if (!cfg) return 'Configurazione non caricata';
    const { semaphore_weights: sw, band_thresholds: bt, kpi_importance: ki } = cfg;
    for (const [k, v] of Object.entries({ ...sw, ...bt, ...ki })) {
      if (v === '' || Number.isNaN(Number(v))) return `Valore mancante o non numerico (${k})`;
    }
    if (!(Number(sw.red) <= Number(sw.yellow) && Number(sw.yellow) <= Number(sw.green))) {
      return 'Pesi semaforo: deve valere rosso ≤ giallo ≤ verde';
    }
    if (!(Number(bt.red_below) <= Number(bt.yellow_below) && Number(bt.yellow_below) <= Number(bt.green_above))) {
      return 'Soglie band: deve valere rosso < giallo < verde';
    }
    if (Number(ki.primary) < Number(ki.secondary)) {
      return 'Pesi KPI: i primari non possono pesare meno dei secondari';
    }
    return null;
  };

  const doSave = async () => {
    const e = validate();
    if (e) { setErr(e); return; }
    setSaving(true); setErr('');
    try {
      const payload = {
        semaphore_weights: cfg.semaphore_weights,
        band_thresholds: cfg.band_thresholds,
        kpi_importance: cfg.kpi_importance,
      };
      const res = await healthScoreConfigApi.update(payload);
      setCfg(res.data); setBaseline(res.data);
      setHealthScoreConfig(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message);
    } finally { setSaving(false); }
  };

  const doReset = async () => {
    setSaving(true); setErr('');
    try {
      const res = await healthScoreConfigApi.reset();
      setCfg(res.data); setBaseline(res.data);
      setHealthScoreConfig(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message);
    } finally { setSaving(false); setResetOpen(false); }
  };

  if (!cfg) return <div className="p-6 text-sm text-slate-500">Caricamento…</div>;

  return (
    <div className="space-y-6" data-testid="health-score-settings">
      <div className="bg-white border border-slate-200 p-6">
        <div className="flex items-start justify-between mb-4 gap-4">
          <div>
            <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
              <Gauge className="w-5 h-5" /> Calibrazione Health Score
            </h2>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Regola pesi semaforo, soglie band e importanza KPI primari/secondari. Le modifiche
              vengono applicate alla prossima lettura delle dashboard (refresh automatico al cambio mese,
              o ricaricamento manuale della pagina).
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!cfg.is_default && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200">
                Personalizzata
              </span>
            )}
            {cfg.is_default && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-slate-50 text-slate-600 border border-slate-200">
                Default
              </span>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <fieldset className="border border-slate-200 p-4">
            <legend className="px-2 text-xs uppercase tracking-wider font-semibold text-slate-600">
              Pesi semaforo (0–100)
            </legend>
            <p className="text-xs text-slate-500 mb-3">
              Valore intrinseco di ogni KPI in base al suo semaforo budget.
            </p>
            {['green', 'yellow', 'red'].map(c => (
              <div key={c} className="flex items-center justify-between mb-2 gap-3">
                <label className="text-sm flex items-center gap-2">
                  <span className={`inline-block w-3 h-3 rounded-full ${c === 'green' ? 'bg-emerald-500' : c === 'yellow' ? 'bg-amber-500' : 'bg-red-500'}`} />
                  {c === 'green' ? 'Verde' : c === 'yellow' ? 'Giallo' : 'Rosso'}
                </label>
                <Input
                  type="number" min="0" max="100" step="1"
                  value={cfg.semaphore_weights[c] ?? ''}
                  onChange={handleNum(`semaphore_weights.${c}`)}
                  className="w-24 rounded-none text-right tabular-nums"
                  data-testid={`hs-weight-${c}`}
                />
              </div>
            ))}
          </fieldset>

          <fieldset className="border border-slate-200 p-4">
            <legend className="px-2 text-xs uppercase tracking-wider font-semibold text-slate-600">
              Soglie band (0–100)
            </legend>
            <p className="text-xs text-slate-500 mb-3">
              Sotto la soglia rossa → 🔴; tra rosso e giallo → 🟡; sopra verde → 🟢. Tra giallo
              e verde serve maggioranza verde sui rossi per il verde.
            </p>
            {[
              { key: 'red_below', label: 'Rosso sotto' },
              { key: 'yellow_below', label: 'Giallo sotto' },
              { key: 'green_above', label: 'Verde oltre' },
            ].map(t => (
              <div key={t.key} className="flex items-center justify-between mb-2 gap-3">
                <label className="text-sm">{t.label}</label>
                <Input
                  type="number" min="0" max="100" step="1"
                  value={cfg.band_thresholds[t.key] ?? ''}
                  onChange={handleNum(`band_thresholds.${t.key}`)}
                  className="w-24 rounded-none text-right tabular-nums"
                  data-testid={`hs-band-${t.key}`}
                />
              </div>
            ))}
          </fieldset>

          <fieldset className="border border-slate-200 p-4">
            <legend className="px-2 text-xs uppercase tracking-wider font-semibold text-slate-600">
              Pesi importanza KPI
            </legend>
            <p className="text-xs text-slate-500 mb-3">
              Quanto contano i KPI primari rispetto ai secondari nella media pesata.
            </p>
            {[
              { key: 'primary', label: 'Primari (vendite, ticket, ricavo orario, occupazione)' },
              { key: 'secondary', label: 'Secondari (servizi, prodotti, recensioni, nuovi)' },
            ].map(p => (
              <div key={p.key} className="mb-3">
                <label className="text-sm block mb-1">{p.label}</label>
                <Input
                  type="number" min="0.1" max="10" step="0.1"
                  value={cfg.kpi_importance[p.key] ?? ''}
                  onChange={handleNum(`kpi_importance.${p.key}`)}
                  className="w-24 rounded-none text-right tabular-nums"
                  data-testid={`hs-importance-${p.key}`}
                />
              </div>
            ))}
          </fieldset>
        </div>

        <div className="mt-6 grid md:grid-cols-3 gap-4 text-xs">
          <div>
            <div className="font-semibold text-slate-600 mb-1">KPI primari</div>
            <div className="text-slate-500">{(cfg.primary_kpis || []).join(', ')}</div>
          </div>
          <div>
            <div className="font-semibold text-slate-600 mb-1">KPI secondari</div>
            <div className="text-slate-500">{(cfg.secondary_kpis || []).join(', ')}</div>
          </div>
          <div>
            <div className="font-semibold text-slate-600 mb-1">Esclusi</div>
            <div className="text-slate-500">{(cfg.excluded_kpis || []).join(', ')}</div>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-6 pt-4 border-t border-slate-200">
          <Button
            onClick={doSave} disabled={saving || !isDirty}
            className="bg-black text-white hover:bg-slate-900 rounded-none"
            data-testid="hs-save-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Salvataggio…' : 'Salva calibrazione'}
          </Button>
          <Button
            variant="outline" onClick={() => setResetOpen(true)} disabled={saving || cfg.is_default}
            className="rounded-none border-slate-300"
            data-testid="hs-reset-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" /> Ripristina default
          </Button>
          {saved && (
            <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1" data-testid="hs-saved-feedback">
              ✓ Salvato
            </span>
          )}
          {err && (
            <span className="text-xs text-red-700 bg-red-50 border border-red-200 px-2 py-1" data-testid="hs-error">
              {err}
            </span>
          )}
        </div>
        {cfg.updated_at && (
          <div className="text-[11px] text-slate-400 mt-3">
            Ultima modifica: {new Date(cfg.updated_at).toLocaleString('it-IT')}{cfg.updated_by && ` da ${cfg.updated_by}`}
          </div>
        )}
      </div>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ripristinare i valori di default?</AlertDialogTitle>
            <AlertDialogDescription>
              Tutti i parametri tornano ai valori storici (verde 100 / giallo 80 / rosso 45,
              soglie 60 / 70 / 75, primari 2 / secondari 1). L'operazione è reversibile
              modificando di nuovo i campi.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doReset} className="bg-black text-white hover:bg-slate-900 rounded-none" data-testid="hs-reset-confirm">
              Ripristina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


/**
 * Pannello Settings · Google Reviews.
 * - Lista store con `google_place_id` (configurati) e rating sync corrente.
 * - Per ciascun store, edit del `place_id` + bottone "Aggiorna ora".
 * - Bottone "Aggiorna tutti": refresh batch di tutto il fleet (usa
 *   `POST /api/admin/google-reviews/refresh`).
 *
 * Pensato per OPS: visibile solo a operations_manager (gate in SettingsPage).
 */
function GoogleReviewsSettings() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [allReport, setAllReport] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingId, setSavingId] = useState(null);
  // Diagnostica
  const [health, setHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [withLive, setWithLive] = useState(false);
  const [realigningId, setRealigningId] = useState(null);
  const [realignTarget, setRealignTarget] = useState(null); // {store_id, display_name, status}

  const loadStores = async () => {
    setLoading(true);
    try {
      const r = await storesApi.list();
      setStores((r.data || []).filter(s => s.active !== false));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadStores(); }, []);

  const handleRefreshAll = async () => {
    setRefreshingAll(true);
    setAllReport(null);
    try {
      const r = await googleReviewsApi.refreshAll();
      setAllReport(r.data);
      await loadStores();
    } finally {
      setRefreshingAll(false);
    }
  };

  const handleRefreshOne = async (storeId) => {
    setRefreshingId(storeId);
    try {
      await googleReviewsApi.refreshStore(storeId);
      await loadStores();
    } finally {
      setRefreshingId(null);
    }
  };

  const startEdit = (s) => {
    setEditingId(s.store_id);
    setEditValue(s.google_place_id || '');
  };

  const saveEdit = async (storeId) => {
    setSavingId(storeId);
    try {
      await storesApi.update(storeId, { google_place_id: editValue.trim() });
      setEditingId(null);
      setEditValue('');
      await loadStores();
    } finally {
      setSavingId(null);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const loadHealth = async () => {
    setLoadingHealth(true);
    try {
      const r = await googleReviewsApi.health(withLive);
      setHealth(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore diagnostica');
    } finally {
      setLoadingHealth(false);
    }
  };

  const doRealign = async () => {
    if (!realignTarget) return;
    setRealigningId(realignTarget.store_id);
    try {
      const r = await googleReviewsApi.realignStore(realignTarget.store_id);
      toast.success(`Riallineato ${realignTarget.display_name} · wipe ${r.data?.wipe?.daily_deleted || 0}d + ${r.data?.wipe?.monthly_deleted || 0}m`);
      setRealignTarget(null);
      await loadHealth();
      await loadStores();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore riallineamento');
    } finally {
      setRealigningId(null);
    }
  };

  const configured = stores.filter(s => s.google_place_id);
  const missing = stores.filter(s => !s.google_place_id);

  if (loading) return <div className="text-sm text-slate-500 p-6">Caricamento…</div>;

  return (
    <div className="space-y-6">
      {/* Header con stats e bottone refresh batch */}
      <div className="bg-white border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-1">Google Reviews</h2>
            <p className="text-sm text-slate-600">
              Sincronizzazione automatica di <strong>rating</strong> e <strong>numero recensioni</strong> da Google Places API per ogni negozio.
              Il batch giornaliero è programmato alle <strong>02:30</strong> Europe/Rome.
            </p>
            <div className="flex items-center gap-4 mt-3 text-sm">
              <span className="text-slate-700"><strong className="tabular-nums">{configured.length}</strong> negozi configurati</span>
              {missing.length > 0 && (
                <span className="text-amber-700"><strong className="tabular-nums">{missing.length}</strong> senza Place ID</span>
              )}
            </div>
          </div>
          <Button
            onClick={handleRefreshAll}
            disabled={refreshingAll || configured.length === 0}
            className="bg-black text-white hover:bg-slate-900 rounded-none"
            data-testid="gr-refresh-all"
          >
            {refreshingAll
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sincronizzo…</>
              : <><RefreshCw className="w-4 h-4 mr-2" /> Aggiorna tutti</>
            }
          </Button>
        </div>
        {allReport && (
          <div className="mt-4 p-3 bg-slate-50 border border-slate-200 text-sm">
            <strong className="text-slate-900">Refresh completato:</strong>{' '}
            <span className="text-emerald-700 tabular-nums">{allReport.ok} ok</span>
            {allReport.failed > 0 && <> · <span className="text-red-700 tabular-nums">{allReport.failed} errori</span></>}
            <> · </> <span className="text-slate-500 tabular-nums">{allReport.total} totali</span>
          </div>
        )}
      </div>

      {/* Tabella negozi */}
      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-4 py-2.5 font-semibold text-slate-700">Negozio</th>
              <th className="px-4 py-2.5 font-semibold text-slate-700">Place ID</th>
              <th className="px-4 py-2.5 font-semibold text-slate-700 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {stores.map(s => {
              const isEditing = editingId === s.store_id;
              return (
                <tr key={s.store_id} data-testid={`gr-row-${s.store_id}`} className="hover:bg-slate-50/60">
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-slate-900">{s.display_name || s.name}</div>
                    {s.code && <div className="text-xs text-slate-400 font-mono">{s.code}</div>}
                  </td>
                  <td className="px-4 py-2.5">
                    {isEditing ? (
                      <Input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        placeholder="ChIJ..."
                        className="font-mono text-xs h-8 rounded-none"
                        data-testid={`gr-input-${s.store_id}`}
                      />
                    ) : s.google_place_id ? (
                      <code className="text-xs text-slate-700 break-all">{s.google_place_id}</code>
                    ) : (
                      <span className="text-xs text-amber-600">Non configurato</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {isEditing ? (
                      <div className="flex justify-end gap-1.5">
                        <Button
                          size="sm" variant="outline"
                          onClick={cancelEdit}
                          className="rounded-none h-8 text-xs"
                          data-testid={`gr-cancel-${s.store_id}`}
                        >Annulla</Button>
                        <Button
                          size="sm" onClick={() => saveEdit(s.store_id)}
                          disabled={savingId === s.store_id}
                          className="bg-black text-white hover:bg-slate-900 rounded-none h-8 text-xs"
                          data-testid={`gr-save-${s.store_id}`}
                        >
                          {savingId === s.store_id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Salva'}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex justify-end gap-1.5">
                        <Button
                          size="sm" variant="outline"
                          onClick={() => startEdit(s)}
                          className="rounded-none h-8 text-xs"
                          data-testid={`gr-edit-${s.store_id}`}
                        >
                          <Pencil className="w-3 h-3 mr-1" />
                          {s.google_place_id ? 'Modifica' : 'Aggiungi'}
                        </Button>
                        {s.google_place_id && (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => handleRefreshOne(s.store_id)}
                            disabled={refreshingId === s.store_id}
                            className="rounded-none h-8 text-xs"
                            data-testid={`gr-refresh-${s.store_id}`}
                          >
                            {refreshingId === s.store_id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <RefreshCw className="w-3 h-3" />}
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Diagnostica salute recensioni */}
      <div className="bg-white border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
          <div>
            <h3 className="text-base font-semibold text-slate-900 mb-1">Salute recensioni</h3>
            <p className="text-xs text-slate-600 max-w-xl">
              Verifica la coerenza tra il numero di recensioni su Google, la cache DB, la somma dei daily e la somma mensile aggregata.
              Se una riga risulta <strong>INFLATED</strong> (over-count) o <strong>DEFLATED</strong>, il pulsante <em>Riallinea</em> ripulisce
              i dati corrotti e ricostruisce lo storico da zero.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
              <input type="checkbox" checked={withLive} onChange={(e) => setWithLive(e.target.checked)} data-testid="gr-health-with-live" />
              Verifica live con Google (consuma API)
            </label>
            <Button
              onClick={loadHealth}
              disabled={loadingHealth}
              variant="outline"
              className="rounded-none h-8 text-xs"
              data-testid="gr-health-run"
            >
              {loadingHealth
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Analizzo…</>
                : <><Activity className="w-3.5 h-3.5 mr-1.5" /> Analizza salute</>
              }
            </Button>
          </div>
        </div>
        {health && (
          <>
            <div className="flex items-center gap-2 flex-wrap text-xs mb-3">
              {Object.entries(health.status_counts || {}).map(([k, v]) => {
                const color = k === 'OK' ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                  : k === 'STALE' ? 'bg-amber-100 text-amber-800 border-amber-300'
                  : k === 'INFLATED' ? 'bg-red-100 text-red-800 border-red-300'
                  : k === 'DEFLATED' ? 'bg-orange-100 text-orange-800 border-orange-300'
                  : 'bg-slate-100 text-slate-700 border-slate-300';
                return (
                  <span key={k} className={`px-2 py-0.5 uppercase tracking-wider font-semibold border ${color}`} data-testid={`gr-health-badge-${k}`}>
                    {k}: <span className="tabular-nums">{v}</span>
                  </span>
                );
              })}
              <span className="text-slate-400 ml-2">Analisi del {new Date(health.checked_at).toLocaleString('it-IT')}</span>
            </div>
            <div className="overflow-x-auto border border-slate-200">
              <table className="w-full text-xs" data-testid="gr-health-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-slate-700">Negozio</th>
                    <th className="px-3 py-2 text-right font-semibold text-slate-700">Cache</th>
                    <th className="px-3 py-2 text-right font-semibold text-slate-700">Daily Σ</th>
                    <th className="px-3 py-2 text-right font-semibold text-slate-700">Monthly Σ</th>
                    {withLive && <th className="px-3 py-2 text-right font-semibold text-slate-700">Live Google</th>}
                    <th className="px-3 py-2 text-right font-semibold text-slate-700">Stato</th>
                    <th className="px-3 py-2 text-right font-semibold text-slate-700">Azione</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(health.stores || []).map(r => {
                    const needsFix = ['INFLATED', 'DEFLATED', 'MONTHLY_DRIFT', 'LIVE_DRIFT'].includes(r.status);
                    const stColor = r.status === 'OK' ? 'text-emerald-700'
                      : r.status === 'STALE' ? 'text-amber-700'
                      : r.status === 'INFLATED' ? 'text-red-700'
                      : r.status === 'DEFLATED' ? 'text-orange-700'
                      : 'text-slate-700';
                    return (
                      <tr key={r.store_id} data-testid={`gr-health-row-${r.store_id}`} className={needsFix ? 'bg-red-50/40' : ''}>
                        <td className="px-3 py-2 font-medium text-slate-900">{r.display_name}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.cache_count ?? '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.daily_sum ?? '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.monthly_sum ?? '—'}</td>
                        {withLive && <td className="px-3 py-2 text-right tabular-nums">{r.live_count ?? (r.live_error ? '❌' : '—')}</td>}
                        <td className={`px-3 py-2 text-right uppercase tracking-wider font-semibold ${stColor}`}>{r.status}</td>
                        <td className="px-3 py-2 text-right">
                          {needsFix && (
                            <Button
                              size="sm" variant="outline"
                              onClick={() => setRealignTarget(r)}
                              disabled={realigningId === r.store_id}
                              className="rounded-none h-7 text-xs border-red-300 text-red-700 hover:bg-red-50"
                              data-testid={`gr-realign-${r.store_id}`}
                            >
                              {realigningId === r.store_id
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : 'Riallinea'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Nota informativa */}
      <div className="bg-emerald-50 border border-emerald-200 p-4 text-sm">
        <p className="font-semibold text-emerald-900 mb-1">Sync attivo</p>
        <p className="text-emerald-800">
          La Google Places API restituisce <strong>rating, numero recensioni</strong> e le <strong>5 recensioni testuali più recenti</strong>
          (con autore, data e contenuto completo). Il batch giornaliero gira alle 02:30 e popola anche
          i KPI mensili <code>avg_rating</code> e <code>review_count</code> con source <code>google_places</code>.
        </p>
      </div>

      <AlertDialog open={!!realignTarget} onOpenChange={(o) => { if (!o) setRealignTarget(null); }}>
        <AlertDialogContent className="rounded-none" data-testid="gr-realign-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Riallineare recensioni · {realignTarget?.display_name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Stato attuale: <strong>{realignTarget?.status}</strong> — Cache <span className="tabular-nums">{realignTarget?.cache_count ?? '—'}</span>, Daily Σ <span className="tabular-nums">{realignTarget?.daily_sum ?? '—'}</span>, Monthly Σ <span className="tabular-nums">{realignTarget?.monthly_sum ?? '—'}</span>.
              <br /><br />
              L&apos;operazione:
              <br />1. <strong>Cancella</strong> tutti i record daily e monthly di <code>review_count</code> per questo negozio.
              <br />2. Fa un <strong>refresh live</strong> da Google Places API (1 chiamata).
              <br />3. <strong>Ricostruisce lo storico</strong> distribuendo il totale attuale sui giorni con dati (dal 1° gennaio 2025).
              <br /><br />
              Al termine <code>daily Σ = monthly Σ = live</code>. Idempotente e non tocca gli altri KPI.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="gr-realign-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={doRealign}
              className="rounded-none bg-red-600 hover:bg-red-700"
              data-testid="gr-realign-apply"
            >
              Riallinea
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================
// Categorie Magazzino (idea utente 2026-05-18)
// CRUD completo su `inventory_categories`. Solo OPS. Lo `slug` (2-8 char)
// viene usato come segmento centrale del codice oggetto BRAND-SLUG-SERIAL,
// quindi è case-sensitive e non modificabile dopo la creazione (il counter
// dei seriali vive su `inventory_counters` con chiave `{brand}-{slug}` e
// rinominare lo slug a posteriori farebbe ripartire il contatore da 1).
// ============================================================
function InventoryCategoriesSettings() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await inventoryApi.categories();
      setCats(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleteError('');
    try {
      await inventoryApi.deleteCategory(toDelete.category_id);
      setToDelete(null);
      await load();
    } catch (e) {
      setDeleteError(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <div data-testid="inventory-cat-settings">
      <div className="bg-white border border-slate-200 p-5 mb-4 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-base font-semibold">Categorie Magazzino</h3>
          <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">
            Le categorie definiscono il tipo di attrezzatura (Forbici, Rasoio, Tagliacapelli…). Lo <b>slug</b> (2-8 caratteri)
            viene incorporato nel codice oggetto: <code className="px-1 py-0.5 bg-slate-100 text-[11px]">BRAND-SLUG-0001</code>.
            Lo slug <b>non è modificabile</b> dopo la creazione perché il counter dei seriali è legato a brand+slug.
          </p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          className="bg-black text-white hover:bg-slate-900 rounded-none shrink-0"
          data-testid="cat-new-btn"
        >
          <Plus className="w-4 h-4 mr-2" />Nuova categoria
        </Button>
      </div>

      <div className="bg-white border border-slate-200" data-testid="cat-list">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-heading text-base font-semibold">Categorie esistenti</h3>
          <span className="text-xs text-slate-500">{cats.length} categorie</span>
        </div>
        {loading ? (
          <p className="px-5 py-6 text-sm text-slate-500">Caricamento…</p>
        ) : cats.length === 0 ? (
          <p className="px-5 py-6 text-sm text-slate-500">Nessuna categoria configurata.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-5 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 w-[100px]">Ordine</th>
                <th className="text-left px-5 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Nome</th>
                <th className="text-left px-5 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 w-[140px]">Slug</th>
                <th className="text-left px-5 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 w-[180px]">Manutenzione cons.</th>
                <th className="text-right px-5 py-2 w-[120px]"></th>
              </tr>
            </thead>
            <tbody>
              {cats.map(c => (
                <tr key={c.category_id} className="border-b border-slate-100" data-testid={`cat-row-${c.slug}`}>
                  <td className="px-5 py-3 text-slate-500 tabular-nums">{c.order ?? 0}</td>
                  <td className="px-5 py-3 font-medium text-slate-900">{c.name}</td>
                  <td className="px-5 py-3">
                    <code className="px-1.5 py-0.5 bg-slate-100 text-[12px] font-mono text-slate-700">{c.slug}</code>
                  </td>
                  <td className="px-5 py-3 text-slate-700 tabular-nums" data-testid={`cat-rec-months-${c.slug}`}>
                    {c.recommended_maintenance_months
                      ? <span>ogni <b>{c.recommended_maintenance_months}</b> {c.recommended_maintenance_months === 1 ? 'mese' : 'mesi'}</span>
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditing(c)}
                      className="rounded-none"
                      data-testid={`cat-edit-btn-${c.slug}`}
                      title="Modifica nome o ordine"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setDeleteError(''); setToDelete(c); }}
                      className="rounded-none text-red-600 hover:bg-red-50 hover:text-red-700"
                      data-testid={`cat-delete-btn-${c.slug}`}
                      title="Archivia categoria"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <CategoryDialog
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}
      {editing && (
        <CategoryDialog
          category={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <AlertDialog open={!!toDelete} onOpenChange={o => { if (!o) { setToDelete(null); setDeleteError(''); } }}>
        <AlertDialogContent className="rounded-none" data-testid="cat-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archivia categoria "{toDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              La categoria verrà disattivata (non più selezionabile nei nuovi oggetti). Gli oggetti esistenti restano associati.
              Se la categoria è già usata da almeno un oggetto, l'archiviazione viene bloccata.
              {deleteError && (
                <span className="block mt-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-xs" data-testid="cat-delete-error">{deleteError}</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="cat-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              className="rounded-none bg-red-600 text-white hover:bg-red-700"
              data-testid="cat-delete-confirm"
            >
              Archivia
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Dialog condiviso create + edit. In edit lo slug è readonly.
function CategoryDialog({ category, onClose, onSaved }) {
  const isEdit = !!category;
  const [name, setName] = useState(category?.name || '');
  const [slug, setSlug] = useState(category?.slug || '');
  const [order, setOrder] = useState(String(category?.order ?? 0));
  const [recMonths, setRecMonths] = useState(
    category?.recommended_maintenance_months != null
      ? String(category.recommended_maintenance_months)
      : ''
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e?.preventDefault();
    setErr('');
    if (!name.trim()) { setErr('Il nome è obbligatorio'); return; }
    if (!slug.trim()) { setErr('Lo slug è obbligatorio'); return; }
    if (slug.length < 2 || slug.length > 8) { setErr('Slug deve essere 2-8 caratteri'); return; }
    const recMonthsNum = recMonths.trim() === '' ? null : parseInt(recMonths, 10);
    if (recMonthsNum !== null && (isNaN(recMonthsNum) || recMonthsNum < 1 || recMonthsNum > 120)) {
      setErr('Manutenzione consigliata: 1-120 mesi (o vuoto)');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(), slug: slug.trim(), order: parseInt(order, 10) || 0,
        recommended_maintenance_months: recMonthsNum,
      };
      if (isEdit) {
        await inventoryApi.updateCategory(category.category_id, payload);
      } else {
        await inventoryApi.createCategory(payload);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent className="rounded-none max-w-md" data-testid="cat-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{isEdit ? `Modifica "${category.name}"` : 'Nuova categoria'}</AlertDialogTitle>
          <AlertDialogDescription>
            {isEdit
              ? 'Aggiorna nome e ordine. Lo slug è bloccato perché legato al counter dei codici oggetto.'
              : 'Crea una nuova categoria di attrezzatura. Lo slug sarà incorporato nel codice (es. BRAND-Slug-0001).'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <form onSubmit={submit} className="space-y-3 py-2">
          <div>
            <label className="text-xs text-slate-600 font-medium">Nome *</label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Es. Asciugacapelli"
              className="rounded-none mt-1"
              data-testid="cat-name-input"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs text-slate-600 font-medium">
              Slug * (2-8 caratteri){isEdit && <span className="ml-1 text-slate-400">— non modificabile</span>}
            </label>
            <Input
              value={slug}
              onChange={e => setSlug(e.target.value.replace(/\s/g, ''))}
              placeholder="Es. Asc"
              className="rounded-none mt-1 font-mono"
              maxLength={8}
              disabled={isEdit}
              data-testid="cat-slug-input"
            />
            {!isEdit && (
              <p className="text-[11px] text-slate-400 mt-1">
                Usato nel codice oggetto. Tipicamente 3 lettere (es. <code>For</code>, <code>Ras</code>, <code>Tag</code>).
              </p>
            )}
          </div>
          <div>
            <label className="text-xs text-slate-600 font-medium">Ordine di visualizzazione</label>
            <Input
              type="number"
              value={order}
              onChange={e => setOrder(e.target.value)}
              className="rounded-none mt-1 w-32"
              data-testid="cat-order-input"
            />
            <p className="text-[11px] text-slate-400 mt-1">Numero più basso = appare prima nella lista.</p>
          </div>
          <div>
            <label className="text-xs text-slate-600 font-medium">Manutenzione consigliata (mesi)</label>
            <Input
              type="number"
              min={1}
              max={120}
              value={recMonths}
              onChange={e => setRecMonths(e.target.value)}
              className="rounded-none mt-1 w-32"
              placeholder="es. 6"
              data-testid="cat-rec-months-input"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Intervallo consigliato tra una manutenzione e l'altra. Lascia vuoto per disabilitare l'avviso automatico.
            </p>
          </div>
          {err && (
            <p className="text-xs text-red-600 px-3 py-2 bg-red-50 border border-red-200" data-testid="cat-form-error">
              {err}
            </p>
          )}
        </form>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none" data-testid="cat-form-cancel" onClick={onClose}>Annulla</AlertDialogCancel>
          <AlertDialogAction
            onClick={submit}
            disabled={saving}
            className="rounded-none bg-black text-white hover:bg-slate-900"
            data-testid="cat-form-submit"
          >
            {saving ? 'Salvataggio…' : isEdit ? 'Salva' : 'Crea categoria'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}



// ============================================================
// Audit RBAC Panel — matrice ruolo×endpoint per compliance
// ============================================================
// Fetcha `GET /api/admin/rbac-audit` e renderizza una tabella raggruppata
// per categoria con badge di colore per i 4 stati (allowed/forbidden/scoped/
// unauthorized). Pensato per export rapido (screenshot/PDF da browser)
// e per dare agli auditor esterni una vista d'insieme della RBAC senza
// dover leggere il codice.
const RBAC_BADGE_TONES = {
  allowed: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  scoped: 'bg-blue-100 text-blue-800 border-blue-300',
  forbidden: 'bg-red-100 text-red-800 border-red-300',
  unauthorized: 'bg-slate-200 text-slate-700 border-slate-300',
};
const RBAC_BADGE_LABEL = {
  allowed: 'Allowed',
  scoped: 'Scoped',
  forbidden: 'Forbidden',
  unauthorized: 'Unauth',
};

function RbacBadge({ status }) {
  const tone = RBAC_BADGE_TONES[status] || 'bg-slate-100 text-slate-600 border-slate-300';
  const label = RBAC_BADGE_LABEL[status] || status;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold border ${tone}`}
      data-testid={`rbac-badge-${status}`}
    >
      {label}
    </span>
  );
}

function RbacAuditPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminApi.rbacAudit()
      .then(r => { setData(r.data); setError(null); })
      .catch(e => setError(e?.response?.data?.detail || 'Errore caricamento audit'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 px-1 py-4" data-testid="rbac-audit-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Caricamento matrice RBAC…
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-sm text-red-700 bg-red-50 border border-red-200 p-3" data-testid="rbac-audit-error">
        {error}
      </div>
    );
  }
  if (!data) return null;

  const groups = data.matrix.reduce((acc, row) => {
    (acc[row.category] = acc[row.category] || []).push(row);
    return acc;
  }, {});
  const groupOrder = Object.keys(groups);

  const downloadFile = (content, filename, type) => {
    const url = URL.createObjectURL(new Blob([content], { type }));
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };
  const stamp = new Date().toISOString().slice(0, 10);
  const exportJson = () => downloadFile(JSON.stringify(data, null, 2), `rbac-audit-${stamp}.json`, 'application/json');
  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['category', 'method', 'path', 'description', 'ops', 'area_manager', 'store_user', 'anonymous', 'since'];
    const lines = [header.join(';'), ...data.matrix.map(r => header.map(k => esc(r[k])).join(';'))];
    downloadFile('\ufeff' + lines.join('\n'), `rbac-audit-${stamp}.csv`, 'text/csv;charset=utf-8');
  };

  return (
    <div className="space-y-6" data-testid="rbac-audit-panel">
      {/* Header con summary */}
      <div className="bg-slate-900 text-white px-5 py-4 flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 mt-0.5 text-emerald-400" />
          <div>
            <h2 className="font-heading text-lg font-semibold">Audit RBAC · Matrice ruolo × endpoint</h2>
            <p className="text-xs text-slate-300 mt-1 max-w-2xl">
              Documentazione dichiarativa del controllo accesso del sistema, per audit
              di compliance. Aggiornata al codice corrente. {data.matrix.length} endpoint mappati su {Object.keys(data.summary.by_category).length} categorie.
            </p>
            <p className="text-[10px] text-slate-400 mt-1">
              Generato il {new Date(data.generated_at).toLocaleString('it-IT')}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3 text-center">
          {['ops', 'area_manager', 'store_user', 'anonymous'].map(role => {
            const r = data.summary.role_totals[role];
            const roleLabel = role === 'ops' ? 'OPS' : role === 'area_manager' ? 'Area Manager' : role === 'store_user' ? 'Store User' : 'Anonimo';
            return (
              <div key={role} className="bg-slate-800 px-3 py-2 min-w-[110px]" data-testid={`rbac-summary-${role}`}>
                <p className="text-[9px] uppercase tracking-wider text-slate-400">{roleLabel}</p>
                <div className="flex items-baseline justify-center gap-1 mt-1">
                  <span className="text-emerald-400 font-mono text-sm font-semibold">{r.allowed}</span>
                  <span className="text-slate-500">·</span>
                  <span className="text-blue-300 font-mono text-sm">{r.scoped}</span>
                  <span className="text-slate-500">·</span>
                  <span className="text-red-300 font-mono text-sm">{r.forbidden}</span>
                  <span className="text-slate-500">·</span>
                  <span className="text-slate-300 font-mono text-sm">{r.unauthorized}</span>
                </div>
                <p className="text-[8px] text-slate-500 mt-1">A · S · F · U</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Export + Legenda */}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="gap-1.5 h-8 rounded-none" onClick={exportJson} data-testid="rbac-export-json">
          <Download className="w-3.5 h-3.5" /> Export JSON
        </Button>
        <Button variant="outline" size="sm" className="gap-1.5 h-8 rounded-none" onClick={exportCsv} data-testid="rbac-export-csv">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </Button>
      </div>
      <div className="bg-slate-50 border border-slate-200 px-4 py-3 text-xs text-slate-600 flex items-center gap-4 flex-wrap" data-testid="rbac-audit-legend">
        <span className="font-semibold uppercase tracking-wider text-[10px] text-slate-500">Legenda</span>
        {Object.entries(data.legend).map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <RbacBadge status={k} />
            <span className="text-slate-600">{v}</span>
          </span>
        ))}
      </div>

      {/* Tabelle per categoria */}
      {groupOrder.map(cat => (
        <div key={cat} className="bg-white border border-slate-200" data-testid={`rbac-category-${cat.replace(/[^a-zA-Z0-9]/g, '-')}`}>
          <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 flex items-center justify-between">
            <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{cat}</h3>
            <span className="text-[10px] text-slate-500">{groups[cat].length} endpoint</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-white border-b border-slate-200">
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2 font-semibold">Method</th>
                  <th className="px-4 py-2 font-semibold">Path</th>
                  <th className="px-4 py-2 font-semibold">Descrizione</th>
                  <th className="px-4 py-2 font-semibold text-center">OPS</th>
                  <th className="px-4 py-2 font-semibold text-center">Area Manager</th>
                  <th className="px-4 py-2 font-semibold text-center">Store User (Staff)</th>
                  <th className="px-4 py-2 font-semibold text-center">Anon</th>
                  <th className="px-4 py-2 font-semibold text-[10px] text-slate-400">Since</th>
                </tr>
              </thead>
              <tbody>
                {groups[cat].map((row, idx) => (
                  <tr key={`${row.method}-${row.path}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                    <td className="px-4 py-2 font-mono text-[10px] text-slate-700 font-semibold">{row.method}</td>
                    <td className="px-4 py-2 font-mono text-[11px] text-slate-900">{row.path}</td>
                    <td className="px-4 py-2 text-slate-600 max-w-md">{row.description}</td>
                    <td className="px-4 py-2 text-center"><RbacBadge status={row.ops} /></td>
                    <td className="px-4 py-2 text-center"><RbacBadge status={row.area_manager} /></td>
                    <td className="px-4 py-2 text-center"><RbacBadge status={row.store_user} /></td>
                    <td className="px-4 py-2 text-center"><RbacBadge status={row.anonymous} /></td>
                    <td className="px-4 py-2 text-[10px] text-slate-400">{row.since}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <p className="text-[11px] text-slate-500 italic px-1">{data.notes}</p>
    </div>
  );
}

// ============================================================
// Fresha Aliases Settings (2026-07-01)
// ============================================================
// Gestione manuale della mappatura `fresha_name → staff_id`. Include:
// - Lista alias esistenti (edit inline / delete)
// - Lista "unmatched" dai recenti import daily (con quick-map)
// - Bottone "Nuovo alias" per crearne uno a mano
function FreshaAliasesSettings() {
  const [aliases, setAliases] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [ignored, setIgnored] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapping, setMapping] = useState(null); // {fresha_name, target_alias_id?}
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState('');

  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'operations_manager' || currentUser?.role === 'hr_manager';

  const load = async () => {
    setLoading(true);
    try {
      const [a, u, g, s] = await Promise.all([
        staffApi.listFreshaAliases(),
        staffApi.listFreshaUnmatched(),
        staffApi.listFreshaIgnored(),
        staffApi.list({ include_deleted: false }),
      ]);
      setAliases(a.data || []);
      setUnmatched(u.data || []);
      setIgnored(g.data || []);
      setStaffList((s.data || []).filter(x => !x.is_deleted && x.status === 'active'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento alias');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSaveMapping = async (staffId) => {
    if (!mapping) return;
    try {
      if (mapping.target_alias_id) {
        // Riassegnazione di alias esistente
        await staffApi.updateFreshaAlias(mapping.target_alias_id, { staff_id: staffId });
        toast.success('Alias aggiornato');
      } else {
        // Nuovo mapping da unmatched o "aggiungi manuale"
        await staffApi.createFreshaAlias({ fresha_name: mapping.fresha_name, staff_id: staffId });
        toast.success(`Alias creato: "${mapping.fresha_name}"`);
      }
      setMapping(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await staffApi.deleteFreshaAlias(deleteTarget.alias_id);
      toast.success('Alias eliminato');
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const handleIgnore = async (name) => {
    if (!window.confirm(`Rimuovere "${name}" dall'elenco dei non mappati?\nNon verrà più segnalato, anche se ricompare negli import futuri. Potrai ripristinarlo dalla sezione "Nomi ignorati".`)) return;
    try {
      await staffApi.ignoreFreshaUnmatched(name);
      toast.success(`"${name}" rimosso dall'elenco`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const handleUnignore = async (name) => {
    try {
      await staffApi.unignoreFreshaUnmatched(name);
      toast.success(`"${name}" ripristinato tra i non mappati`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };


  const filteredAliases = useMemo(() => {
    if (!search.trim()) return aliases;
    const q = search.toLowerCase();
    return aliases.filter(a =>
      (a.fresha_name || '').toLowerCase().includes(q) ||
      (a.staff_name || '').toLowerCase().includes(q)
    );
  }, [aliases, search]);

  if (loading) {
    return <div className="text-slate-500 p-4">Caricamento…</div>;
  }

  return (
    <div className="space-y-6" data-testid="fresha-aliases-panel">
      {/* Intro */}
      <div>
        <h3 className="text-base font-semibold text-slate-900 mb-1">Alias Fresha · mappa nomi troncati ai dipendenti</h3>
        <p className="text-xs text-slate-600 max-w-3xl">
          Fresha esporta i nomi in modo abbreviato (es. &quot;Mario R.&quot;) o con soli iniziali. Qui gestisci la mappatura persistente tra il nome Fresha e il dipendente reale.
          Una volta salvato, l&apos;alias verrà riutilizzato per <strong>tutti gli import successivi</strong> (mensile e daily via estensione Chrome).
        </p>
      </div>

      {/* Sezione Unmatched (mostra sempre) */}
      <div className="bg-white border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-0.5 flex items-center gap-2">
              Non mappati
              {unmatched.length > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-800 border border-red-300 tabular-nums" data-testid="unmatched-count-badge">
                  {unmatched.length}
                </span>
              )}
            </h4>
            <p className="text-xs text-slate-500">Nomi Fresha visti nei recenti import auto (Chrome extension) che non hanno un alias salvato. Le loro metriche non vengono importate finché non li mappi.</p>
          </div>
        </div>
        {unmatched.length === 0 ? (
          <p className="text-xs text-emerald-700 flex items-center gap-1.5" data-testid="unmatched-empty">
            <CheckCircle2 className="w-3.5 h-3.5" /> Nessun nome non mappato. Tutti gli import daily processano correttamente.
          </p>
        ) : (
          <div className="overflow-x-auto border border-slate-200">
            <table className="w-full text-xs" data-testid="unmatched-table">
              <thead className="bg-red-50 border-b border-red-200">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-800">Nome Fresha</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-800">Ultimo import</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-800">Volte visto</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-800">Azione</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {unmatched.map(u => (
                  <tr key={u.fresha_name} data-testid={`unmatched-row-${u.fresha_name}`}>
                    <td className="px-3 py-2 font-medium text-slate-900">{u.fresha_name}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">{u.last_seen}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-600">{u.seen_count}</td>
                    <td className="px-3 py-2 text-right">
                      {isAdmin ? (
                        <div className="inline-flex gap-1">
                          <Button
                            size="sm" variant="outline"
                            onClick={() => setMapping({ fresha_name: u.fresha_name })}
                            className="rounded-none h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            data-testid={`map-btn-${u.fresha_name}`}
                          >
                            <Plus className="w-3 h-3 mr-1" /> Mappa
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            onClick={() => handleIgnore(u.fresha_name)}
                            title="Rimuovi dall'elenco (non verrà più segnalato)"
                            className="rounded-none h-7 text-xs border-slate-300 text-slate-500 hover:bg-slate-100"
                            data-testid={`ignore-btn-${u.fresha_name}`}
                          >
                            <EyeOff className="w-3 h-3 mr-1" /> Ignora
                          </Button>
                        </div>
                      ) : (
                        <span className="text-slate-400 text-[11px]">Solo OPS/HR</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sezione Nomi ignorati (collassata) */}
      {ignored.length > 0 && (
        <details className="bg-white border border-slate-200" data-testid="ignored-section">
          <summary className="cursor-pointer p-4 text-sm font-semibold text-slate-700 select-none">
            Nomi ignorati ({ignored.length})
            <span className="text-xs font-normal text-slate-400 ml-2">esclusi dall&apos;elenco non mappati e dagli avvisi</span>
          </summary>
          <div className="border-t border-slate-200 divide-y divide-slate-100">
            {ignored.map(g => (
              <div key={g.fresha_name} className="px-4 py-2 flex items-center gap-3 text-xs" data-testid={`ignored-row-${g.fresha_name}`}>
                <span className="font-medium text-slate-700 flex-1">{g.fresha_name}</span>
                <span className="text-slate-400 tabular-nums">ignorato il {(g.ignored_at || '').slice(0, 10)}</span>
                {isAdmin && (
                  <Button
                    size="sm" variant="outline"
                    onClick={() => handleUnignore(g.fresha_name)}
                    className="rounded-none h-7 text-xs"
                    data-testid={`unignore-btn-${g.fresha_name}`}
                  >
                    <RotateCcw className="w-3 h-3 mr-1" /> Ripristina
                  </Button>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Sezione Alias attivi */}
      <div className="bg-white border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-0.5">Alias attivi ({aliases.length})</h4>
            <p className="text-xs text-slate-500">Ogni riga associa un nome Fresha ad un dipendente reale. Modifica o rimuovi liberamente.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca…" className="rounded-none pl-7 h-8 text-xs w-40"
                data-testid="alias-search"
              />
            </div>
            {isAdmin && (
              <Button
                size="sm" variant="outline"
                onClick={() => setMapping({ fresha_name: '' })}
                className="rounded-none h-8 text-xs"
                data-testid="alias-new-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Nuovo alias
              </Button>
            )}
          </div>
        </div>
        {filteredAliases.length === 0 ? (
          <p className="text-xs text-slate-500 italic">Nessun alias {search ? 'corrispondente' : 'salvato'}.</p>
        ) : (
          <div className="overflow-x-auto border border-slate-200">
            <table className="w-full text-xs" data-testid="aliases-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700">Nome Fresha</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700">Dipendente</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700">Creato</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredAliases.map(a => (
                  <tr key={a.alias_id} data-testid={`alias-row-${a.alias_id}`} className={a.staff_active === false ? 'bg-amber-50/40' : ''}>
                    <td className="px-3 py-2 font-medium text-slate-900">{a.fresha_name}</td>
                    <td className="px-3 py-2 text-slate-700">
                      {a.staff_name || <span className="text-red-600 italic">Dipendente non trovato/cancellato</span>}
                      {a.staff_active === false && a.staff_name && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-700">non attivo</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-500 tabular-nums text-[11px]">{a.created_at?.slice(0, 10)}</td>
                    <td className="px-3 py-2 text-right">
                      {isAdmin && (
                        <div className="inline-flex gap-1">
                          <button
                            onClick={() => setMapping({ fresha_name: a.fresha_name, target_alias_id: a.alias_id })}
                            title="Riassegna"
                            className="p-1.5 text-slate-400 hover:text-blue-700 hover:bg-blue-50"
                            data-testid={`alias-edit-${a.alias_id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(a)}
                            title="Elimina alias"
                            className="p-1.5 text-slate-400 hover:text-red-700 hover:bg-red-50"
                            data-testid={`alias-delete-${a.alias_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Nota */}
      <div className="bg-blue-50 border border-blue-200 p-4 text-sm">
        <p className="font-semibold text-blue-900 mb-1">Come si popolano gli alias automaticamente?</p>
        <p className="text-blue-800 text-xs">
          Quando fai un <strong>import mensile</strong> Fresha (`Import → Fresha Performance`), il sistema propone il match fuzzy tra i nomi Fresha e i dipendenti. Al commit, ogni match confermato viene salvato qui come alias e riusato per gli import successivi (mensili e daily). Puoi anche mappare a mano da questa pagina.
        </p>
      </div>

      {/* Dialog: mappatura */}
      <MapAliasDialog
        open={!!mapping}
        target={mapping}
        staffList={staffList}
        onClose={() => setMapping(null)}
        onSave={handleSaveMapping}
      />

      {/* AlertDialog: delete */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent className="rounded-none" data-testid="alias-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare alias &quot;{deleteTarget?.fresha_name}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>
              Da questo momento in poi il nome <code>{deleteTarget?.fresha_name}</code> nell&apos;export Fresha risulterà <strong>non mappato</strong>: i dati di quel dipendente NON verranno importati finché non ricrei l&apos;alias.
              <br /><br />
              I dati già importati in passato (performance mensili, KPI giornalieri) restano invariati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="alias-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-none bg-red-600 hover:bg-red-700" data-testid="alias-delete-apply">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MapAliasDialog({ open, target, staffList, onClose, onSave }) {
  const [freshaName, setFreshaName] = useState('');
  const [staffId, setStaffId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target) {
      setFreshaName(target.fresha_name || '');
      setStaffId('');
    }
  }, [target]);

  const isNewFromScratch = target && !target.target_alias_id && !target.fresha_name;
  const canEditFreshaName = isNewFromScratch;
  const canSubmit = freshaName.trim().length > 0 && staffId.length > 0 && !saving;

  const submit = async () => {
    setSaving(true);
    try { await onSave(staffId); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none sm:max-w-lg" data-testid="map-alias-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {target?.target_alias_id ? 'Riassegna alias' : (isNewFromScratch ? 'Nuovo alias' : `Mappa "${target?.fresha_name}"`)}
          </DialogTitle>
          <DialogDescription>
            Associa il nome esportato da Fresha ad un dipendente reale. L&apos;alias sarà usato per tutti gli import successivi.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 block mb-1">Nome Fresha *</label>
            <Input
              value={freshaName}
              onChange={(e) => setFreshaName(e.target.value)}
              disabled={!canEditFreshaName}
              placeholder="Es. Mario R."
              className="rounded-none"
              autoFocus={isNewFromScratch}
              data-testid="map-alias-fresha-input"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 block mb-1">Dipendente *</label>
            <StaffCombobox
              value={staffId}
              onChange={setStaffId}
              staffList={staffList}
              placeholder="Scegli un dipendente…"
              testId="map-alias-staff-select"
              className="w-full"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" className="rounded-none" onClick={onClose} disabled={saving} data-testid="map-alias-cancel">Annulla</Button>
          <Button
            onClick={submit}
            disabled={!canSubmit}
            className="rounded-none bg-black text-white hover:bg-slate-900 disabled:opacity-40"
            data-testid="map-alias-save"
          >
            {saving ? 'Salvo…' : (target?.target_alias_id ? 'Riassegna' : 'Salva alias')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
